import React, { useState, useEffect, useRef } from "react";
import "./Style.css";
import "./Typography.css";
import "./Position.css";
import { DEVICES } from "../../constants/breakpoints";
import Borders from "./Borders";
import Background from "./Background";
import Effects from "./Effects";
import StyleState from "./StyleState";
import CreateClassName from "./CreateClassName";
import CustomCssEditor from "./CustomCssEditor";

const Style = ({
  selectedElement,
  updateElementState,
  updateElementStyles,
  updateElementClassName,
  elements,
  globalClassNames,
  addToGlobalClassNames,
  setSelectedElement,
  selectedDevice,
  onDeviceSelect,
  classStylesRegistry,
  setClassStylesRegistry,
  appliedStylesRegistry,
  setAppliedStylesRegistry,
  setPages,
  currentPageId,
  getDefaultStyles,
  setElements,
  combinedClassStylesRegistry,
  setCombinedClassStylesRegistry,
  assets,
  siteFonts // Add siteFonts prop
}) => {
  const [classNames, setClassNames] = useState(
    selectedElement?.className ? selectedElement.className.split(" ") : []
  );
  const [isEditing, setIsEditing] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [showDropdown, setShowDropdown] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isDisplayDropdownOpen, setIsDisplayDropdownOpen] = useState(false);
  const [isFlexDropdownOpen, setIsFlexDropdownOpen] = useState(false);
  const [isFlexAlignXDropdownOpen, setIsFlexAlignXDropdownOpen] =
    useState(false);
  const [isFlexAlignYDropdownOpen, setIsFlexAlignYDropdownOpen] =
    useState(false);
  const [isGridAlignXDropdownOpen, setIsGridAlignXDropdownOpen] =
    useState(false);
  const [isGridAlignYDropdownOpen, setIsGridAlignYDropdownOpen] =
    useState(false);
  const [popupInput, setPopupInput] = useState({
    isOpen: false,
    value: "",
    property: "",
    position: { x: 0, y: 0 },
    unit: "px"
  });
  const [propertyRanges, setPropertyRanges] = useState({
    paddingLeft: { min: -100, max: 100 },
    paddingRight: { min: -100, max: 100 },
    paddingTop: { min: -100, max: 100 },
    paddingBottom: { min: -100, max: 100 },
    marginLeft: { min: -100, max: 100 },
    marginRight: { min: -100, max: 100 },
    marginTop: { min: -100, max: 100 },
    marginBottom: { min: -100, max: 100 },
  });
  const [devicePropertyRanges, setDevicePropertyRanges] = useState({
    [DEVICES.DESKTOP]: {
      paddingLeft: { min: -100, max: 100 },
      paddingRight: { min: -100, max: 100 },
      paddingTop: { min: -100, max: 100 },
      paddingBottom: { min: -100, max: 100 },
      marginLeft: { min: -100, max: 100 },
      marginRight: { min: -100, max: 100 },
      marginTop: { min: -100, max: 100 },
      marginBottom: { min: -100, max: 100 },
    },
    [DEVICES.TABLET]: {
      paddingLeft: { min: -100, max: 100 },
      paddingRight: { min: -100, max: 100 },
      paddingTop: { min: -100, max: 100 },
      paddingBottom: { min: -100, max: 100 },
      marginLeft: { min: -100, max: 100 },
      marginRight: { min: -100, max: 100 },
      marginTop: { min: -100, max: 100 },
      marginBottom: { min: -100, max: 100 },
    },
    [DEVICES.MOBILE]: {
      paddingLeft: { min: -100, max: 100 },
      paddingRight: { min: -100, max: 100 },
      paddingTop: { min: -100, max: 100 },
      paddingBottom: { min: -100, max: 100 },
      marginLeft: { min: -100, max: 100 },
      marginRight: { min: -100, max: 100 },
      marginTop: { min: -100, max: 100 },
      marginBottom: { min: -100, max: 100 },
    },
  });
  const [isWidthUnitDropdownOpen, setIsWidthUnitDropdownOpen] = useState(false);
  const [isHeightUnitDropdownOpen, setIsHeightUnitDropdownOpen] =
    useState(false);
  const sizeUnits = [
    "px",
    "%",
    "em",
    "rem",
    "ch",
    "vw",
    "vh",
    "svw",
    "svh",
    "auto",
  ];
  const [widthInput, setWidthInput] = useState("");
  const [heightInput, setHeightInput] = useState("");
  const [widthUnit, setWidthUnit] = useState("px");
  const [heightUnit, setHeightUnit] = useState("px");
  const [minWidthInput, setMinWidthInput] = useState("");
  const [minHeightInput, setMinHeightInput] = useState("");
  const [minWidthUnit, setMinWidthUnit] = useState("px");
  const [minHeightUnit, setMinHeightUnit] = useState("px");
  const [isMinWidthUnitDropdownOpen, setIsMinWidthUnitDropdownOpen] =
    useState(false);
  const [isMinHeightUnitDropdownOpen, setIsMinHeightUnitDropdownOpen] =
    useState(false);
  const [maxWidthInput, setMaxWidthInput] = useState("");
  const [maxHeightInput, setMaxHeightInput] = useState("");
  const [maxWidthUnit, setMaxWidthUnit] = useState("px");
  const [maxHeightUnit, setMaxHeightUnit] = useState("px");
  const [isMaxWidthUnitDropdownOpen, setIsMaxWidthUnitDropdownOpen] =
    useState(false);
  const [isMaxHeightUnitDropdownOpen, setIsMaxHeightUnitDropdownOpen] =
    useState(false);
  const [overflow, setOverflow] = useState(
    selectedElement?.styles?.[selectedDevice]?.overflow || "visible"
  );
  const [objectFit, setObjectFit] = useState(
    selectedElement?.styles?.[selectedDevice]?.objectFit || "fill"
  );
  const [isObjectFitDropdownOpen, setIsObjectFitDropdownOpen] = useState(false);
  const [isFontDropdownOpen, setIsFontDropdownOpen] = useState(false);
  const [selectedFont, setSelectedFont] = useState(
    selectedElement?.styles?.[selectedDevice]?.fontFamily || ""
  );
  const [isFontWeightDropdownOpen, setIsFontWeightDropdownOpen] =
    useState(false);
  const [selectedFontWeight, setSelectedFontWeight] = useState(
    selectedElement?.styles?.[selectedDevice]?.fontWeight || ""
  );
  const [fontSize, setFontSize] = useState("");
  const [fontSizeUnit, setFontSizeUnit] = useState("px");
  const [isFontSizeUnitDropdownOpen, setIsFontSizeUnitDropdownOpen] =
    useState(false);
  const [lineHeight, setLineHeight] = useState("");
  const [lineHeightUnit, setLineHeightUnit] = useState("px");
  const [isLineHeightUnitDropdownOpen, setIsLineHeightUnitDropdownOpen] =
    useState(false);
  const initialColor = selectedElement?.styles?.[selectedDevice]?.color ||
    selectedElement?.styles?.color ||
    "#000000";
  // Extract initial alpha value if color is rgba
  const getInitialAlpha = () => {
    if (selectedElement && selectedElement.styles) {
      const elementColor = selectedElement.styles.color;
      
      // Check if the color is in rgba format
      if (elementColor && elementColor.startsWith('rgba')) {
        // Extract alpha value from rgba color
        const match = elementColor.match(/rgba\s*\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*,\s*([0-9.]+)\s*\)/);
        if (match && match[1]) {
          return parseFloat(match[1]);
        }
      }
    }
    // Default to 1 (100% opacity) if no rgba color found or no element selected
    return 1;
  };
  const [textColor, setTextColor] = useState(initialColor);
  const [colorAlpha, setColorAlpha] = useState(getInitialAlpha());
  const [isRgba, setIsRgba] = useState(initialColor.startsWith('rgba'));
  const [position, setPosition] = useState(
    selectedElement?.styles?.[selectedDevice]?.position || "static"
  );
  const [positionOffsets, setPositionOffsets] = useState({
    top: { value: "", unit: "px" },
    right: { value: "", unit: "px" },
    bottom: { value: "", unit: "px" },
    left: { value: "", unit: "px" },
  });
  const [textAlign, setTextAlign] = useState(
    selectedElement?.styles?.[selectedDevice]?.textAlign || "left"
  );
  const [textDecoration, setTextDecoration] = useState(
    selectedElement?.styles?.[selectedDevice]?.textDecoration || "none"
  );

  // Create a list of fonts that includes custom fonts from siteFonts
  const getAvailableFonts = () => {
    // Extract custom fonts from siteFonts
    const customFonts = siteFonts && siteFonts.length > 0
      ? siteFonts.map(fontRef => fontRef.font.family)
      : [];
    
    // Standard fonts
    const standardFonts = [
      "Arial, sans-serif",
      "Helvetica, sans-serif",
      "Times New Roman, serif",
      "Georgia, serif",
      "Courier New, monospace",
      "Verdana, sans-serif",
      "Tahoma, sans-serif",
      "Trebuchet MS, sans-serif",
      "Impact, sans-serif",
      "system-ui"
    ];
    
    // Combine custom fonts with standard fonts, with custom fonts at the top
    return [...customFonts, ...standardFonts];
  };
  
  const fonts = getAvailableFonts();

  const fontWeights = [
    { value: "100", label: "100 - Thin" },
    { value: "200", label: "200 - Extra Light" },
    { value: "300", label: "300 - Light" },
    { value: "400", label: "400 - Normal" },
    { value: "500", label: "500 - Medium" },
    { value: "600", label: "600 - Semi-Bold" },
    { value: "700", label: "700 - Bold" },
    { value: "800", label: "800 - Extra-Bold" },
    { value: "900", label: "900 - Black" },
  ];

  const fontUnits = ["px", "em", "rem", "%", "vw", "vh", "svw", "svh", "ch", "ex", "pt", "pc", "cm", "mm", "in"];

  const [zIndex, setZIndex] = useState(
    selectedElement?.styles?.[selectedDevice]?.zIndex || ""
  );

  const [float, setFloat] = useState(
    selectedElement?.styles?.[selectedDevice]?.float || "none"
  );
  const [clear, setClear] = useState(
    selectedElement?.styles?.[selectedDevice]?.clear || "none"
  );
  const [gapUnit, setGapUnit] = useState("px");
  const [isGapUnitDropdownOpen, setIsGapUnitDropdownOpen] = useState(false);

  useEffect(() => {
    if (selectedElement && selectedElement.type === "div") {
      if (!selectedElement.styles?.display) {
        updateElementStyles(selectedElement.id, "display", "block");
      }
      if (
        selectedElement.styles?.display === "flex" &&
        !selectedElement.styles?.flexDirection
      ) {
        // updateElementStyles(selectedElement.id, "flexDirection", "row");
        // updateElementStyles(selectedElement.id, "justifyContent", "flex-start");
        // updateElementStyles(selectedElement.id, "alignItems", "stretch");
      }
      if (
        selectedElement.styles?.display === "grid" &&
        !selectedElement.styles?.gridTemplateColumns
      ) {
        updateElementStyles(
          selectedElement.id,
          "gridTemplateColumns",
          "repeat(1, 1fr)"
        );
        updateElementStyles(
          selectedElement.id,
          "gridTemplateRows",
          "repeat(1, 1fr)"
        );
        updateElementStyles(selectedElement.id, "justifyItems", "stretch");
        updateElementStyles(selectedElement.id, "alignItems", "stretch");
      }
    }
  }, [selectedElement?.id]);

  useEffect(() => {
    if (selectedElement) {
      const widthValue = selectedElement.styles?.[selectedDevice]?.width;
      if (widthValue === "auto") {
        setWidthInput("auto");
        setWidthUnit("auto");
      } else if (widthValue) {
        setWidthInput(widthValue.replace(/[^0-9]/g, ""));
        setWidthUnit(widthValue.replace(/[0-9]/g, "") || "px");
      } else if (selectedElement.styles?.width) {
        setWidthInput(selectedElement.styles.width.replace(/[^0-9]/g, ""));
        setWidthUnit(
          selectedElement.styles.width.replace(/[0-9]/g, "") || "px"
        );
      } else {
        setWidthInput("");
        setWidthUnit("px");
      }

      const heightValue = selectedElement.styles?.[selectedDevice]?.height;
      if (heightValue === "auto") {
        setHeightInput("auto");
        setHeightUnit("auto");
      } else if (heightValue) {
        setHeightInput(heightValue.replace(/[^0-9]/g, ""));
        setHeightUnit(heightValue.replace(/[0-9]/g, "") || "px");
      } else if (selectedElement.styles?.height) {
        setHeightInput(selectedElement.styles.height.replace(/[^0-9]/g, ""));
        setHeightUnit(
          selectedElement.styles.height.replace(/[0-9]/g, "") || "px"
        );
      } else {
        setHeightInput("");
        setHeightUnit("px");
      }

      const minWidthValue = selectedElement.styles?.[selectedDevice]?.minWidth;
      if (minWidthValue === "auto") {
        setMinWidthInput("auto");
        setMinWidthUnit("auto");
      } else if (minWidthValue) {
        setMinWidthInput(minWidthValue.replace(/[^0-9]/g, ""));
        setMinWidthUnit(minWidthValue.replace(/[0-9]/g, "") || "px");
      } else if (selectedElement.styles?.minWidth) {
        setMinWidthInput(
          selectedElement.styles.minWidth.replace(/[^0-9]/g, "")
        );
        setMinWidthUnit(
          selectedElement.styles.minWidth.replace(/[0-9]/g, "") || "px"
        );
      } else {
        setMinWidthInput("");
        setMinWidthUnit("px");
      }

      const minHeightValue =
        selectedElement.styles?.[selectedDevice]?.minHeight;
      if (minHeightValue === "auto") {
        setMinHeightInput("auto");
        setMinHeightUnit("auto");
      } else if (minHeightValue) {
        setMinHeightInput(minHeightValue.replace(/[^0-9]/g, ""));
        setMinHeightUnit(minHeightValue.replace(/[0-9]/g, "") || "px");
      } else if (selectedElement.styles?.minHeight) {
        setMinHeightInput(
          selectedElement.styles.minHeight.replace(/[^0-9]/g, "")
        );
        setMinHeightUnit(
          selectedElement.styles.minHeight.replace(/[0-9]/g, "") || "px"
        );
      } else {
        setMinHeightInput("");
        setMinHeightUnit("px");
      }

      const maxWidthValue = selectedElement.styles?.[selectedDevice]?.maxWidth;
      if (maxWidthValue === "auto") {
        setMaxWidthInput("auto");
        setMaxWidthUnit("auto");
      } else if (maxWidthValue) {
        setMaxWidthInput(maxWidthValue.replace(/[^0-9]/g, ""));
        setMaxWidthUnit(maxWidthValue.replace(/[0-9]/g, "") || "px");
      } else if (selectedElement.styles?.maxWidth) {
        setMaxWidthInput(
          selectedElement.styles.maxWidth.replace(/[^0-9]/g, "")
        );
        setMaxWidthUnit(
          selectedElement.styles.maxWidth.replace(/[0-9]/g, "") || "px"
        );
      } else {
        setMaxWidthInput("");
        setMaxWidthUnit("px");
      }

      const maxHeightValue =
        selectedElement.styles?.[selectedDevice]?.maxHeight;
      if (maxHeightValue === "auto") {
        setMaxHeightInput("auto");
        setMaxHeightUnit("auto");
      } else if (maxHeightValue) {
        setMaxHeightInput(maxHeightValue.replace(/[^0-9]/g, ""));
        setMaxHeightUnit(maxHeightValue.replace(/[0-9]/g, "") || "px");
      } else if (selectedElement.styles?.maxHeight) {
        setMaxHeightInput(
          selectedElement.styles.maxHeight.replace(/[^0-9]/g, "")
        );
        setMaxHeightUnit(
          selectedElement.styles.maxHeight.replace(/[0-9]/g, "") || "px"
        );
      } else {
        setMaxHeightInput("");
        setMaxHeightUnit("px");
      }

      const overflowValue = selectedElement.styles?.[selectedDevice]?.overflow;
      if (overflowValue) {
        setOverflow(overflowValue);
      } else if (selectedElement.styles?.overflow) {
        setOverflow(selectedElement.styles.overflow);
      } else {
        setOverflow("visible");
      }

      const objectFitValue =
        selectedElement.styles?.[selectedDevice]?.objectFit;
      if (objectFitValue) {
        setObjectFit(objectFitValue);
      } else if (selectedElement.styles?.objectFit) {
        setObjectFit(selectedElement.styles.objectFit);
      } else {
        setObjectFit("fill");
      }

      const fontValue = selectedElement.styles?.[selectedDevice]?.fontFamily;
      if (fontValue) {
        setSelectedFont(fontValue);
      } else if (selectedElement.styles?.fontFamily) {
        setSelectedFont(selectedElement.styles.fontFamily);
      } else {
        setSelectedFont("");
      }

      const fontWeightValue =
        selectedElement.styles?.[selectedDevice]?.fontWeight;
      if (fontWeightValue) {
        setSelectedFontWeight(fontWeightValue);
      } else if (selectedElement.styles?.fontWeight) {
        setSelectedFontWeight(selectedElement.styles.fontWeight);
      } else {
        setSelectedFontWeight("");
      }

      const fontSizeValue = selectedElement.styles?.[selectedDevice]?.fontSize;
      if (fontSizeValue) {
        setFontSize(fontSizeValue.replace(/[^0-9]/g, ""));
        setFontSizeUnit(fontSizeValue.replace(/[0-9]/g, "") || "px");
      } else if (selectedElement.styles?.fontSize) {
        setFontSize(selectedElement.styles.fontSize.replace(/[^0-9]/g, ""));
        setFontSizeUnit(
          selectedElement.styles.fontSize.replace(/[0-9]/g, "") || "px"
        );
      } else {
        setFontSize("");
        setFontSizeUnit("px");
      }

      const lineHeightValue =
        selectedElement.styles?.[selectedDevice]?.lineHeight;
      if (lineHeightValue) {
        setLineHeight(lineHeightValue.replace(/[^0-9]/g, ""));
        setLineHeightUnit(lineHeightValue.replace(/[0-9]/g, "") || "px");
      } else if (selectedElement.styles?.lineHeight) {
        setLineHeight(selectedElement.styles.lineHeight.replace(/[^0-9]/g, ""));
        setLineHeightUnit(
          selectedElement.styles.lineHeight.replace(/[0-9]/g, "") || "px"
        );
      } else {
        setLineHeight("");
        setLineHeightUnit("px");
      }

      const gapValue = selectedElement.styles?.[selectedDevice]?.gap;
      if (gapValue) {
        setGapUnit(gapValue.replace(/[0-9]/g, "") || "px");
      } else if (selectedElement.styles?.gap) {
        setGapUnit(selectedElement.styles.gap.replace(/[0-9]/g, "") || "px");
      } else {
        setGapUnit("px");
      }

      const colorValue = selectedElement.styles?.[selectedDevice]?.color;
      if (colorValue) {
        setTextColor(colorValue);
      } else if (selectedElement.styles?.color) {
        setTextColor(selectedElement.styles.color);
      } else {
        setTextColor("#000000");
      }

      const textAlignValue =
        selectedElement.styles?.[selectedDevice]?.textAlign;
      if (textAlignValue) {
        setTextAlign(textAlignValue);
      } else if (selectedElement.styles?.textAlign) {
        setTextAlign(selectedElement.styles.textAlign);
      } else {
        setTextAlign("left");
      }

      const textDecorationValue =
        selectedElement.styles?.[selectedDevice]?.textDecoration;
      if (textDecorationValue) {
        setTextDecoration(textDecorationValue);
      } else if (selectedElement.styles?.textDecoration) {
        setTextDecoration(selectedElement.styles.textDecoration);
      } else {
        setTextDecoration("none");
      }

      const positionValue = selectedElement.styles?.[selectedDevice]?.position;
      if (positionValue) {
        setPosition(positionValue);
      } else if (selectedElement.styles?.position) {
        setPosition(selectedElement.styles.position);
      } else {
        setPosition("static");
      }

      ["top", "right", "bottom", "left"].forEach((side) => {
        const value =
          selectedElement.styles?.[selectedDevice]?.[side] ||
          selectedElement.styles?.[side];

        if (value === "auto") {
          setPositionOffsets((prev) => ({
            ...prev,
            [side]: {
              value: "",
              unit: "auto",
            },
          }));
        } else if (value) {
          setPositionOffsets((prev) => ({
            ...prev,
            [side]: {
              value: value.replace(/[^0-9-]/g, ""),
              unit: value.replace(/[0-9-]/g, "") || "px",
            },
          }));
        } else {
          setPositionOffsets((prev) => ({
            ...prev,
            [side]: {
              value: "",
              unit: "px",
            },
          }));
        }
      });

      const zIndexValue = selectedElement.styles?.[selectedDevice]?.zIndex;
      if (zIndexValue) {
        setZIndex(zIndexValue);
      } else if (selectedElement.styles?.zIndex) {
        setZIndex(selectedElement.styles.zIndex);
      } else {
        setZIndex("");
      }

      const floatValue = selectedElement.styles?.[selectedDevice]?.float;
      if (floatValue) {
        setFloat(floatValue);
      } else if (selectedElement.styles?.float) {
        setFloat(selectedElement.styles.float);
      } else {
        setFloat("none");
      }

      const clearValue = selectedElement.styles?.[selectedDevice]?.clear;
      if (clearValue) {
        setClear(clearValue);
      } else if (selectedElement.styles?.clear) {
        setClear(selectedElement.styles.clear);
      } else {
        setClear("none");
      }
    }
  }, [selectedElement, selectedDevice]);

  // Update color alpha when a new element is selected
  useEffect(() => {
    if (selectedElement) {
      const alpha = getInitialAlpha();
      setColorAlpha(alpha);
      
      // Also update textColor state if the element has a color
      if (selectedElement.styles && selectedElement.styles.color) {
        setTextColor(selectedElement.styles.color);
        setIsRgba(selectedElement.styles.color.startsWith('rgba'));
      }
    }
  }, [selectedElement]);

  const getGridUnitCount = (templateString) => {
    if (!templateString) return 1;
    const match = templateString.match(/repeat\((\d+),\s*1fr\)/);
    return match ? parseInt(match[1]) : 1;
  };

  const handleDragStart = (e, property) => {
    // Prevent text selection during dragging
    e.preventDefault();
    
    // Track starting position
    const startX = e.clientX;
    
    // Store the starting value for this property
    let startValue;
    
    // Handle margin and padding properties
    if (property.startsWith('margin') || property.startsWith('padding')) {
      // Get the current style value
      const currentStyle = selectedElement.styles?.[selectedDevice]?.[property] || 
                         selectedElement.styles?.[property] || "0px";
      
      // Handle auto value
      if (currentStyle === "auto") {
        startValue = 0;
      } else {
        // Extract the numeric value
        const match = currentStyle.match(/^(-?\d+(\.\d+)?)/);
        startValue = match ? parseInt(match[0]) : 0;
      }
    } else {
      switch (property) {
        case "fontSize":
          startValue = parseInt(fontSize) || 0;
          break;
        case "lineHeight":
          startValue = parseInt(lineHeight) || 0;
          break;
        case "top":
          startValue = parseInt(positionOffsets.top.value) || 0;
          break;
        case "right":
          startValue = parseInt(positionOffsets.right.value) || 0;
          break;
        case "bottom":
          startValue = parseInt(positionOffsets.bottom.value) || 0;
          break;
        case "left":
          startValue = parseInt(positionOffsets.left.value) || 0;
          break;
        case "zIndex":
          startValue = parseInt(zIndex) || 0;
          break;
        case "width":
          startValue = parseInt(widthInput) || 0;
          break;
        case "height":
          startValue = parseInt(heightInput) || 0;
          break;
        case "minWidth":
          startValue = parseInt(minWidthInput) || 0;
          break;
        case "minHeight":
          startValue = parseInt(minHeightInput) || 0;
          break;
        case "maxWidth":
          startValue = parseInt(maxWidthInput) || 0;
          break;
        case "maxHeight":
          startValue = parseInt(maxHeightInput) || 0;
          break;
        default:
          startValue = 0;
      }
    }

    const handleDrag = (moveEvent) => {
      const currentX = moveEvent.clientX;
      const deltaX = currentX - startX;
      
      // Calculate new value - move by 1 unit for every 2 pixels of drag
      let newValue;
      
      // Handle margin and padding properties
      if (property.startsWith('margin') || property.startsWith('padding')) {
        // For margin properties, allow negative values
        if (property.startsWith('margin')) {
          newValue = startValue + Math.floor(deltaX / 2);
        } else {
          // For padding, keep the value non-negative
          newValue = Math.max(0, startValue + Math.floor(deltaX / 2));
        }
        
        // Get the current style to extract the unit
        const currentStyle = selectedElement.styles?.[selectedDevice]?.[property] || 
                           selectedElement.styles?.[property] || "0px";
        
        // Extract the unit from the current style
        const styleUnitMatch = currentStyle.match(/[a-z%]+$/i);
        const styleUnit = styleUnitMatch ? styleUnitMatch[0] : "px";
        
        // If unit is not auto, update with the new value
        if (styleUnit !== "auto") {
          updateElementStyles(
            selectedElement.id,
            property,
            `${newValue}${styleUnit}`,
            null,
            selectedDevice
          );
        }
      } else {
        // For non-margin/padding properties, keep the old behavior
        newValue = Math.max(0, startValue + Math.floor(deltaX / 2));
        
        // Update the right UI state and element styles based on property
        switch (property) {
          case "fontSize":
            handleFontSizeChange(newValue.toString());
            break;
          case "lineHeight":
            handleLineHeightChange(newValue.toString());
            break;
          case "top":
            handlePositionOffsetsChange("top", newValue.toString());
            break;
          case "right":
            handlePositionOffsetsChange("right", newValue.toString());
            break;
          case "bottom":
            handlePositionOffsetsChange("bottom", newValue.toString());
            break;
          case "left":
            handlePositionOffsetsChange("left", newValue.toString());
            break;
          case "zIndex":
            handleZIndexChange(newValue.toString());
            break;
          case "width":
            handleWidthChange(newValue.toString());
            break;
          case "height":
            handleHeightChange(newValue.toString());
            break;
          case "minWidth":
            handleMinWidthChange(newValue.toString());
            break;
          case "minHeight":
            handleMinHeightChange(newValue.toString());
            break;
          case "maxWidth":
            handleMaxWidthChange(newValue.toString());
            break;
          case "maxHeight":
            handleMaxHeightChange(newValue.toString());
            break;
          default:
            break;
        }
      }
    };

    const handleDragEnd = () => {
      document.removeEventListener("mousemove", handleDrag);
      document.removeEventListener("mouseup", handleDragEnd);
    };

    document.addEventListener("mousemove", handleDrag);
    document.addEventListener("mouseup", handleDragEnd);
  };

  const handleNumberClick = (e, property) => {
    e.preventDefault();
    
    // For margin and padding properties, open the popup input
    if (property.startsWith('margin') || property.startsWith('padding')) {
      // Get the current value
      const currentValue = selectedElement.styles?.[selectedDevice]?.[property] || 
                         selectedElement.styles?.[property] || "0px";
      
      // Parse the value to get the number part without unit
      const valueMatch = currentValue.match(/^(-?\d+(\.\d+)?)/);
      const value = valueMatch ? valueMatch[0] : "0";
      
      // Get the unit from the current value
      const unitMatch = currentValue.match(/[a-z%]+$/i);
      const unit = unitMatch ? unitMatch[0] : "px";
      
      // Open the popup with the current values
      setPopupInput({
        isOpen: true,
        value: value,
        property: property,
        position: { x: e.clientX, y: e.clientY },
        unit: unit === "auto" ? "px" : unit
      });
      
      return;
    }
    
    switch (property) {
      case "fontSize":
        if (fontSize === "0" || fontSize === "") {
          handleFontSizeChange("16");
        } else {
          handleFontSizeChange("0");
        }
        break;
      case "lineHeight":
        if (lineHeight === "0" || lineHeight === "") {
          handleLineHeightChange("1.2");
        } else {
          handleLineHeightChange("0");
        }
        break;
      case "top":
      case "right":
      case "bottom":
      case "left":
        const currentValue = positionOffsets[property].value;
        const currentUnit = positionOffsets[property].unit;
        
        if (currentUnit === "auto") {
          // Switch from auto to 0px
          handlePositionOffsetsChange(property, "0");
          handlePositionUnitChange(property, "px");
        } else if (currentValue === "0" || currentValue === "") {
          // Switch from 0px to auto
          handlePositionUnitChange(property, "auto");
        } else {
          // Set to 0 with current unit
          handlePositionOffsetsChange(property, "0");
        }
        break;
      case "width":
        if (widthUnit === "auto") {
          // Switch from auto to 0px
          handleWidthChange("0");
          handleWidthUnitChange("px");
        } else if (widthInput === "0" || widthInput === "") {
          // Switch from 0px to auto
          handleWidthUnitChange("auto");
        } else {
          // Set to 0 with current unit
          handleWidthChange("0");
        }
        break;
      case "height":
        if (heightUnit === "auto") {
          // Switch from auto to 0px
          handleHeightChange("0");
          handleHeightUnitChange("px");
        } else if (heightInput === "0" || heightInput === "") {
          // Switch from 0px to auto
          handleHeightUnitChange("auto");
        } else {
          // Set to 0 with current unit
          handleHeightChange("0");
        }
        break;
      case "minWidth":
        if (minWidthUnit === "auto") {
          // Switch from auto to 0px
          handleMinWidthChange("0");
          handleMinWidthUnitChange("px");
        } else if (minWidthInput === "0" || minWidthInput === "") {
          // Switch from 0px to auto
          handleMinWidthUnitChange("auto");
        } else {
          // Set to 0 with current unit
          handleMinWidthChange("0");
        }
        break;
      case "minHeight":
        if (minHeightUnit === "auto") {
          // Switch from auto to 0px
          handleMinHeightChange("0");
          handleMinHeightUnitChange("px");
        } else if (minHeightInput === "0" || minHeightInput === "") {
          // Switch from 0px to auto
          handleMinHeightUnitChange("auto");
        } else {
          // Set to 0 with current unit
          handleMinHeightChange("0");
        }
        break;
      case "maxWidth":
        if (maxWidthUnit === "auto") {
          // Switch from auto to 0px
          handleMaxWidthChange("0");
          handleMaxWidthUnitChange("px");
        } else if (maxWidthInput === "0" || maxWidthInput === "") {
          // Switch from 0px to auto
          handleMaxWidthUnitChange("auto");
        } else {
          // Set to 0 with current unit
          handleMaxWidthChange("0");
        }
        break;
      case "maxHeight":
        if (maxHeightUnit === "auto") {
          // Switch from auto to 0px
          handleMaxHeightChange("0");
          handleMaxHeightUnitChange("px");
        } else if (maxHeightInput === "0" || maxHeightInput === "") {
          // Switch from 0px to auto
          handleMaxHeightUnitChange("auto");
        } else {
          // Set to 0 with current unit
          handleMaxHeightChange("0");
        }
        break;
      case "zIndex":
        if (zIndex === "0" || zIndex === "") {
          handleZIndexChange("1");
        } else {
          handleZIndexChange("0");
        }
        break;
      default:
        break;
    }
  };

  const handleDisplayChange = (value) => {
    updateElementStyles(
      selectedElement.id,
      "display",
      value,
      null,
      selectedDevice
    );
  };

  const handleOverflowChange = (value) => {
    updateElementStyles(
      selectedElement.id,
      "overflow",
      value,
      null,
      selectedDevice
    );
  };

  const handleObjectFitChange = (value) => {
    updateElementStyles(
      selectedElement.id,
      "objectFit",
      value,
      null,
      selectedDevice
    );
  };

  const handleFontChange = (value) => {
    // Apply the font family
    updateElementStyles(
      selectedElement.id,
      "fontFamily",
      value,
      null,
      selectedDevice
    );
    
    // If it's a custom font, check if there are specific weight options
    if (value && siteFonts && siteFonts.length > 0) {
      const customFont = siteFonts.find(fontRef => 
        fontRef.font && value.includes(fontRef.font.family)
      );
      
      // If we found a custom font with a default variant, apply the weight
      if (customFont && customFont.font.variants && customFont.font.variants.length > 0) {
        // Look for regular weight first
        const hasRegular = customFont.font.variants.includes('regular') || 
                          customFont.font.variants.includes('400');
        
        if (hasRegular) {
          updateElementStyles(
            selectedElement.id,
            "fontWeight",
            "400",
            null,
            selectedDevice
          );
        } else {
          // Use the first available variant as fallback
          const firstVariant = customFont.font.variants[0];
          // Extract weight number from variant (e.g. "700" from "700italic")
          const weightMatch = firstVariant.match(/^(\d+)/);
          if (weightMatch) {
            updateElementStyles(
              selectedElement.id,
              "fontWeight",
              weightMatch[1],
              null,
              selectedDevice
            );
          }
        }
      }
    }
  };

  const handleFontWeightChange = (value) => {
    updateElementStyles(
      selectedElement.id,
      "fontWeight",
      value,
      null,
      selectedDevice
    );
  };

  const handleFontSizeChange = (value) => {
    const newValue = `${value}${fontSizeUnit}`;
    updateElementStyles(
      selectedElement.id,
      "fontSize",
      newValue,
      null,
      selectedDevice
    );
  };

  const handleFontSizeUnitChange = (value) => {
    setFontSizeUnit(value);
    const newValue = fontSize ? `${fontSize}${value}` : "";
    updateElementStyles(
      selectedElement.id,
      "fontSize",
      newValue,
      null,
      selectedDevice
    );
  };

  const handleLineHeightChange = (value) => {
    const newValue = `${value}${lineHeightUnit}`;
    updateElementStyles(
      selectedElement.id,
      "lineHeight",
      newValue,
      null,
      selectedDevice
    );
  };

  const handleLineHeightUnitChange = (value) => {
    setLineHeightUnit(value);
    const newValue = lineHeight ? `${lineHeight}${value}` : "";
    updateElementStyles(
      selectedElement.id,
      "lineHeight",
      newValue,
      null,
      selectedDevice
    );
  };

  const handleTextColorChange = (value, alpha = null) => {
    // If alpha is not explicitly provided, use the current value
    const finalAlpha = alpha !== null ? alpha : colorAlpha;
    
    // Update color state
    setTextColor(value);
    
    // Check if the value is already in rgba format
    if (value.startsWith('rgba')) {
      // If it is, replace the existing alpha value
      const newColor = value.replace(/rgba\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*,\s*[0-9.]+\s*\)/, 
        `rgba($1, $2, $3, ${finalAlpha})`);
      setIsRgba(true);
      updateElementStyles(
        selectedElement.id,
        "color",
        newColor,
        null,
        selectedDevice
      );
    } else {
      // If it's a hex or rgb color, convert to rgba
      const rgb = hexToRgb(value);
      if (rgb) {
        const { r, g, b } = rgb;
        const rgba = `rgba(${r}, ${g}, ${b}, ${finalAlpha})`;
        setIsRgba(true);
        updateElementStyles(
          selectedElement.id,
          "color",
          rgba,
          null,
          selectedDevice
        );
      } else {
        // If conversion fails, use as is (might be a named color or other format)
        updateElementStyles(
          selectedElement.id,
          "color",
          value,
          null,
          selectedDevice
        );
        setIsRgba(false);
      }
    }
  };

  const handleTextAlignChange = (value) => {
    updateElementStyles(
      selectedElement.id,
      "textAlign",
      value,
      null,
      selectedDevice
    );
  };

  const handleTextDecorationChange = (value) => {
    updateElementStyles(
      selectedElement.id,
      "textDecoration",
      value,
      null,
      selectedDevice
    );
  };

  const handlePositionChange = (value) => {
    updateElementStyles(
      selectedElement.id,
      "position",
      value,
      null,
      selectedDevice
    );
  };

  const handlePositionOffsetsChange = (side, value) => {
    // Update the value in state
    setPositionOffsets((prev) => ({
      ...prev,
      [side]: { ...prev[side], value },
    }));
    
    // If the value is empty, don't update the style yet (wait for onBlur)
    if (value === "") return;
    
    // If the unit is auto, just set auto as the value
    if (positionOffsets[side].unit === "auto") {
      updateElementStyles(
        selectedElement.id,
        side,
        "auto",
        null,
        selectedDevice
      );
      return;
    }
    
    // Otherwise update with the value + unit
    updateElementStyles(
      selectedElement.id,
      side,
      `${value}${positionOffsets[side].unit}`,
      null,
      selectedDevice
    );
  };

  const handleZIndexChange = (value) => {
    setZIndex(value);
    updateElementStyles(
      selectedElement.id,
      "zIndex",
      value,
      null,
      selectedDevice
    );
  };

  const handleFloatChange = (value) => {
    setFloat(value);
    updateElementStyles(
      selectedElement.id,
      "float",
      value,
      null,
      selectedDevice
    );
  };

  const handleClearChange = (value) => {
    setClear(value);
    updateElementStyles(
      selectedElement.id,
      "clear",
      value,
      null,
      selectedDevice
    );
  };

  const handlePositionUnitChange = (side, unit) => {
    setPositionOffsets((prev) => ({
      ...prev,
      [side]: { ...prev[side], unit },
    }));

    const newValue = unit === "auto"
      ? "auto"
      : positionOffsets[side].value
        ? `${positionOffsets[side].value}${unit}`
        : "";

    updateElementStyles(
      selectedElement.id,
      side,
      newValue,
      null,
      selectedDevice
    );
  };

  const handleWidthChange = (value) => {
    setWidthInput(value);
    
    // If the value is empty, don't update the style yet (wait for onBlur)
    if (value === "") return;
    
    // If the unit is auto, just set auto as the value
    if (widthUnit === "auto") {
      updateElementStyles(
        selectedElement.id,
        "width",
        "auto",
        null,
        selectedDevice
      );
      return;
    }
    
    // Otherwise update with the value + unit
    updateElementStyles(
      selectedElement.id,
      "width",
      `${value}${widthUnit}`,
      null,
      selectedDevice
    );
  };

  const handleWidthUnitChange = (unit) => {
    setWidthUnit(unit);

    const newValue = unit === "auto"
      ? "auto"
      : widthInput
        ? `${widthInput}${unit}`
        : "";

    updateElementStyles(
      selectedElement.id,
      "width",
      newValue,
      null,
      selectedDevice
    );
  };

  const handleHeightChange = (value) => {
    setHeightInput(value);
    
    // If the value is empty, don't update the style yet (wait for onBlur)
    if (value === "") return;
    
    // If the unit is auto, just set auto as the value
    if (heightUnit === "auto") {
      updateElementStyles(
        selectedElement.id,
        "height",
        "auto",
        null,
        selectedDevice
      );
      return;
    }
    
    // Otherwise update with the value + unit
    updateElementStyles(
      selectedElement.id,
      "height",
      `${value}${heightUnit}`,
      null,
      selectedDevice
    );
  };

  const handleHeightUnitChange = (unit) => {
    setHeightUnit(unit);

    const newValue = unit === "auto"
      ? "auto"
      : heightInput
        ? `${heightInput}${unit}`
        : "";

    updateElementStyles(
      selectedElement.id,
      "height",
      newValue,
      null,
      selectedDevice
    );
  };

  const handleMinWidthChange = (value) => {
    setMinWidthInput(value);
    
    // If the value is empty, don't update the style yet (wait for onBlur)
    if (value === "") return;
    
    // If the unit is auto, just set auto as the value
    if (minWidthUnit === "auto") {
      updateElementStyles(
        selectedElement.id,
        "minWidth",
        "auto",
        null,
        selectedDevice
      );
      return;
    }
    
    // Otherwise update with the value + unit
    updateElementStyles(
      selectedElement.id,
      "minWidth",
      `${value}${minWidthUnit}`,
      null,
      selectedDevice
    );
  };

  const handleMinHeightChange = (value) => {
    setMinHeightInput(value);
    
    // If the value is empty, don't update the style yet (wait for onBlur)
    if (value === "") return;
    
    // If the unit is auto, just set auto as the value
    if (minHeightUnit === "auto") {
      updateElementStyles(
        selectedElement.id,
        "minHeight",
        "auto",
        null,
        selectedDevice
      );
      return;
    }
    
    // Otherwise update with the value + unit
    updateElementStyles(
      selectedElement.id,
      "minHeight",
      `${value}${minHeightUnit}`,
      null,
      selectedDevice
    );
  };

  const handleMinWidthUnitChange = (value) => {
    setMinWidthUnit(value);
    
    const newValue = value === "auto"
      ? "auto"
      : minWidthInput
        ? `${minWidthInput}${value}`
        : "";

    updateElementStyles(
      selectedElement.id,
      "minWidth",
      newValue,
      null,
      selectedDevice
    );
  };

  const handleMinHeightUnitChange = (value) => {
    setMinHeightUnit(value);
    
    const newValue = value === "auto"
      ? "auto"
      : minHeightInput
        ? `${minHeightInput}${value}`
        : "";

    updateElementStyles(
      selectedElement.id,
      "minHeight",
      newValue,
      null,
      selectedDevice
    );
  };

  const handleMaxWidthChange = (value) => {
    setMaxWidthInput(value);
    
    // If the value is empty, don't update the style yet (wait for onBlur)
    if (value === "") return;
    
    // If the unit is auto, just set auto as the value
    if (maxWidthUnit === "auto") {
      updateElementStyles(
        selectedElement.id,
        "maxWidth",
        "auto",
        null,
        selectedDevice
      );
      return;
    }
    
    // Otherwise update with the value + unit
    updateElementStyles(
      selectedElement.id,
      "maxWidth",
      `${value}${maxWidthUnit}`,
      null,
      selectedDevice
    );
  };

  const handleMaxHeightChange = (value) => {
    setMaxHeightInput(value);
    
    // If the value is empty, don't update the style yet (wait for onBlur)
    if (value === "") return;
    
    // If the unit is auto, just set auto as the value
    if (maxHeightUnit === "auto") {
      updateElementStyles(
        selectedElement.id,
        "maxHeight",
        "auto",
        null,
        selectedDevice
      );
      return;
    }
    
    // Otherwise update with the value + unit
    updateElementStyles(
      selectedElement.id,
      "maxHeight",
      `${value}${maxHeightUnit}`,
      null,
      selectedDevice
    );
  };

  const handleMaxWidthUnitChange = (value) => {
    setMaxWidthUnit(value);
    
    const newValue = value === "auto"
      ? "auto"
      : maxWidthInput
        ? `${maxWidthInput}${value}`
        : "";

    updateElementStyles(
      selectedElement.id,
      "maxWidth",
      newValue,
      null,
      selectedDevice
    );
  };

  const handleMaxHeightUnitChange = (value) => {
    setMaxHeightUnit(value);
    
    const newValue = value === "auto"
      ? "auto"
      : maxHeightInput
        ? `${maxHeightInput}${value}`
        : "";

    updateElementStyles(
      selectedElement.id,
      "maxHeight",
      newValue,
      null,
      selectedDevice
    );
  };

  const handleGapUnitChange = (value) => {
    setGapUnit(value);
    // Extract value from gap property
    const gapValue = selectedElement.styles?.gap
      ? selectedElement.styles.gap.replace(/[^0-9]/g, "")
      : "";
    const newValue = gapValue ? `${gapValue}${value}` : "";
    updateElementStyles(
      selectedElement.id,
      "gap",
      newValue,
      null,
      selectedDevice
    );
  };

  // Add new function to handle unit change in popup
  const handlePopupUnitChange = (unit) => {
    setPopupInput((prev) => ({ ...prev, unit }));
    
    // Only update styles if there's a value and it's not "auto"
    if (popupInput.value && popupInput.value !== "auto") {
      updateElementStyles(
        selectedElement.id,
        popupInput.property,
        `${popupInput.value}${unit}`,
        null,
        selectedDevice
      );
    }
  };

  const handleAlphaChange = (event) => {
    const newAlpha = parseFloat(event.target.value);
    setColorAlpha(newAlpha);
    
    // Apply the new alpha value to the current text color
    handleTextColorChange(textColor, newAlpha);
  };

  const rgbaToHex = (rgba) => {
    // Check if the color is already in hex format
    if (rgba.startsWith('#')) return rgba;
    
    // Extract RGB values from rgba format
    const match = rgba.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(?:0|1|0?\.\d+))?\)/);
    if (!match) return "#000000"; // Default fallback
    
    // Convert RGB to hex
    const r = parseInt(match[1]).toString(16).padStart(2, '0');
    const g = parseInt(match[2]).toString(16).padStart(2, '0');
    const b = parseInt(match[3]).toString(16).padStart(2, '0');
    
    return `#${r}${g}${b}`;
  };

  // Utility function to convert hex to rgb
  const hexToRgb = (hex) => {
    // Remove # if present
    hex = hex.replace('#', '');
    
    // Handle both 3-digit and 6-digit hex
    if (hex.length === 3) {
      hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }
    
    if (hex.length !== 6) {
      return null;
    }
    
    // Convert to RGB values
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    
    return { r, g, b };
  };

  if (!selectedElement) return null;

  const isListElement =
    selectedElement.type === "list" || selectedElement.type === "listItem";

  // Add a helper function to get font display name
  const getFontDisplayName = (fontFamily) => {
    if (!fontFamily) return '';
    
    // First check if this is one of our custom fonts
    if (siteFonts && siteFonts.length > 0) {
      const customFont = siteFonts.find(f => 
        f.font && fontFamily.includes(f.font.family.replace(/"/g, '').split(',')[0].trim())
      );
      if (customFont) return customFont.font.name;
    }
    
    // Handle standard fonts
    const fontMap = {
      'Arial, sans-serif': 'Arial',
      'Helvetica, sans-serif': 'Helvetica',
      'Times New Roman, serif': 'Times New Roman',
      'Georgia, serif': 'Georgia',
      'Courier New, monospace': 'Courier New',
      'Verdana, sans-serif': 'Verdana',
      'Tahoma, sans-serif': 'Tahoma',
      'Trebuchet MS, sans-serif': 'Trebuchet MS',
      'Impact, sans-serif': 'Impact',
      'system-ui': 'System UI'
    };
    
    return fontMap[fontFamily] || fontFamily;
  };

  return (
    <div className="style-panel">
      <div className="style-render">
        <div className="property-section">
          <div className="section-header state-section-header">
            {/* <h4 className="section-title-basic-properties">Switch State</h4> */}
            <StyleState
              selectedElement={selectedElement}
              updateElementState={updateElementState}
              setSelectedElement={setSelectedElement}
              classNames={classNames}
              setClassNames={setClassNames}
            />
          </div>

          <CreateClassName
            selectedElement={selectedElement}
            updateElementClassName={updateElementClassName}
            elements={elements}
            globalClassNames={globalClassNames}
            addToGlobalClassNames={addToGlobalClassNames}
            classNames={classNames}
            setClassNames={setClassNames}
            classStylesRegistry={classStylesRegistry}
            setClassStylesRegistry={setClassStylesRegistry}
            combinedClassStylesRegistry={combinedClassStylesRegistry}
            setCombinedClassStylesRegistry={setCombinedClassStylesRegistry}
            setPages={setPages}
            currentPageId={currentPageId}
            selectedDevice={selectedDevice}
            setSelectedElement={setSelectedElement}
            getDefaultStyles={getDefaultStyles}
          />
        </div>
        {isListElement && (
          <div className="property-section">
            <h4>List Style</h4>
            <div className="property-group">
              <label>List Style Type</label>
              <select
                value={selectedElement.styles?.listStyleType || "disc"}
                onChange={(e) =>
                  updateElementStyles(
                    selectedElement.id,
                    "listStyleType",
                    e.target.value
                  )
                }
              >
                <option value="none">None</option>
                <option value="disc">Disc (•)</option>
                <option value="circle">Circle (○)</option>
                <option value="square">Square (■)</option>
                <option value="decimal">Numbers (1, 2, 3)</option>
                <option value="lower-alpha">Letters (a, b, c)</option>
                <option value="upper-alpha">Letters (A, B, C)</option>
                <option value="lower-roman">Roman (i, ii, iii)</option>
                <option value="upper-roman">Roman (I, II, III)</option>
              </select>
            </div>
            <div className="property-group">
              <label>List Style Position</label>
              <select
                value={selectedElement.styles?.listStylePosition || "outside"}
                onChange={(e) =>
                  updateElementStyles(
                    selectedElement.id,
                    "listStylePosition",
                    e.target.value
                  )
                }
              >
                <option value="outside">Outside</option>
                <option value="inside">Inside</option>
              </select>
            </div>
            {selectedElement.type === "list" && (
              <div className="property-group">
                <label>List Indent</label>
                <input
                  type="number"
                  value={parseInt(selectedElement.styles?.paddingLeft) || 20}
                  onChange={(e) =>
                    updateElementStyles(
                      selectedElement.id,
                      "paddingLeft",
                      `${e.target.value}px`
                    )
                  }
                />
              </div>
            )}
          </div>
        )}
        <div className="property-section">
          <h4>Layout</h4>
          <div className="display-property">
            <span className="display-label">Display</span>
            <div className="display-options">
              <button
                className={`display-option ${
                  selectedElement.styles?.[selectedDevice]?.display ===
                    "block" ||
                  (!selectedElement.styles?.[selectedDevice]?.display &&
                    selectedElement.styles?.display === "block") ||
                  (!selectedElement.styles?.[selectedDevice]?.display &&
                    !selectedElement.styles?.display &&
                    selectedElement.type === "div")
                    ? "active"
                    : ""
                }`}
                onClick={() => handleDisplayChange("block")}
              >
                Block
              </button>

              <button
                className={`display-option ${
                  selectedElement.styles?.[selectedDevice]?.display ===
                    "flex" ||
                  (!selectedElement.styles?.[selectedDevice]?.display &&
                    selectedElement.styles?.display === "flex")
                    ? "active"
                    : ""
                }`}
                onClick={() => handleDisplayChange("flex")}
              >
                Flex
              </button>

              <button
                className={`display-option ${
                  selectedElement.styles?.[selectedDevice]?.display ===
                    "grid" ||
                  (!selectedElement.styles?.[selectedDevice]?.display &&
                    selectedElement.styles?.display === "grid")
                    ? "active"
                    : ""
                }`}
                onClick={() => handleDisplayChange("grid")}
              >
                Grid
              </button>

              <div className="display-option-dropdown">
                <button
                  className={`display-option ${
                    [
                      "none",
                      "inline",
                      "inline-block",
                      "inline-flex",
                      "inline-grid",
                    ].includes(
                      selectedElement.styles?.[selectedDevice]?.display
                    ) ||
                    (!selectedElement.styles?.[selectedDevice]?.display &&
                      [
                        "none",
                        "inline",
                        "inline-block",
                        "inline-flex",
                        "inline-grid",
                      ].includes(selectedElement.styles?.display))
                      ? "active"
                      : ""
                  }`}
                  onClick={() =>
                    setIsDisplayDropdownOpen(!isDisplayDropdownOpen)
                  }
                >
                  {selectedElement.styles?.[selectedDevice]?.display &&
                  [
                    "none",
                    "inline",
                    "inline-block",
                    "inline-flex",
                    "inline-grid",
                  ].includes(selectedElement.styles[selectedDevice].display)
                    ? selectedElement.styles[selectedDevice].display
                    : !selectedElement.styles?.[selectedDevice]?.display &&
                      selectedElement.styles?.display &&
                      [
                        "none",
                        "inline",
                        "inline-block",
                        "inline-flex",
                        "inline-grid",
                      ].includes(selectedElement.styles.display)
                    ? selectedElement.styles.display
                    : "none"}{" "}
                  <span className="dropdown-arrow">
                    {isDisplayDropdownOpen ? "▼" : "▶"}
                  </span>
                </button>

                {isDisplayDropdownOpen && (
                  <div className="display-dropdown-content">
                    <button onClick={() => handleDisplayChange("none")}>
                      None
                    </button>
                    <button onClick={() => handleDisplayChange("inline")}>
                      Inline
                    </button>
                    <button onClick={() => handleDisplayChange("inline-block")}>
                      Inline Block
                    </button>
                    <button onClick={() => handleDisplayChange("inline-flex")}>
                      Inline Flex
                    </button>
                    <button onClick={() => handleDisplayChange("inline-grid")}>
                      Inline Grid
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {selectedElement.styles?.display === "flex" && (
            <div className="flex-properties">
              <div className="flex-property">
                <span className="flex-label">Direction</span>
                <div className="flex-options">
                  <button
                    className={`flex-option ${
                      selectedElement.styles?.flexDirection === "row"
                        ? "active"
                        : ""
                    }`}
                    onClick={() =>
                      updateElementStyles(
                        selectedElement.id,
                        "flexDirection",
                        "row"
                      )
                    }
                  >
                    Horizontal
                  </button>

                  <button
                    className={`flex-option ${
                      selectedElement.styles?.flexDirection === "column"
                        ? "active"
                        : ""
                    }`}
                    onClick={() =>
                      updateElementStyles(
                        selectedElement.id,
                        "flexDirection",
                        "column"
                      )
                    }
                  >
                    Vertical
                  </button>

                  <div className="flex-option-dropdown">
                    <button
                      className={`flex-option ${
                        ["row-reverse", "column-reverse"].includes(
                          selectedElement.styles?.flexDirection
                        )
                          ? "active"
                          : ""
                      }`}
                      onClick={() => setIsFlexDropdownOpen(!isFlexDropdownOpen)}
                    >
                      {["row-reverse", "column-reverse"].includes(
                        selectedElement.styles?.flexDirection
                      )
                        ? selectedElement.styles?.flexDirection ===
                          "row-reverse"
                          ? "Right to Left"
                          : "Bottom to Top"
                        : "More"}
                      <span
                        className={`dropdown-arrow ${
                          isFlexDropdownOpen ? "open" : ""
                        }`}
                      >
                        ▼
                      </span>
                    </button>
                    {isFlexDropdownOpen && (
                      <div className="flex-dropdown-content">
                        <button
                          onClick={() =>
                            updateElementStyles(
                              selectedElement.id,
                              "flexDirection",
                              "row-reverse"
                            )
                          }
                        >
                          Right to Left
                        </button>
                        <button
                          onClick={() =>
                            updateElementStyles(
                              selectedElement.id,
                              "flexDirection",
                              "column-reverse"
                            )
                          }
                        >
                          Bottom to Top
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex-property">
                <span className="flex-label">Align</span>
                <div className="flex-align-options">
                  <div className="flex-align-dropdown">
                    <button
                      className="flex-align-button"
                      onClick={() =>
                        setIsFlexAlignXDropdownOpen(!isFlexAlignXDropdownOpen)
                      }
                    >
                      <span className="align-axis-label">X</span>
                      <span className="align-value">
                        {selectedElement.styles?.justifyContent === "flex-start"
                          ? "Left"
                          : selectedElement.styles?.justifyContent === "center"
                          ? "Center"
                          : selectedElement.styles?.justifyContent ===
                            "flex-end"
                          ? "Right"
                          : selectedElement.styles?.justifyContent ===
                            "space-between"
                          ? "Space Between"
                          : selectedElement.styles?.justifyContent ===
                            "space-around"
                          ? "Space Around"
                          : "Left"}
                      </span>
                      <span
                        className={`dropdown-arrow ${
                          isFlexAlignXDropdownOpen ? "open" : ""
                        }`}
                      >
                        ▼
                      </span>
                    </button>
                    {isFlexAlignXDropdownOpen && (
                      <div className="flex-align-dropdown-content">
                        <button
                          onClick={() =>
                            updateElementStyles(
                              selectedElement.id,
                              "justifyContent",
                              "flex-start"
                            )
                          }
                        >
                          Left
                        </button>
                        <button
                          onClick={() =>
                            updateElementStyles(
                              selectedElement.id,
                              "justifyContent",
                              "center"
                            )
                          }
                        >
                          Center
                        </button>
                        <button
                          onClick={() =>
                            updateElementStyles(
                              selectedElement.id,
                              "justifyContent",
                              "flex-end"
                            )
                          }
                        >
                          Right
                        </button>
                        <button
                          onClick={() =>
                            updateElementStyles(
                              selectedElement.id,
                              "justifyContent",
                              "space-between"
                            )
                          }
                        >
                          Space Between
                        </button>
                        <button
                          onClick={() =>
                            updateElementStyles(
                              selectedElement.id,
                              "justifyContent",
                              "space-around"
                            )
                          }
                        >
                          Space Around
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="flex-align-dropdown">
                    <button
                      className="flex-align-button"
                      onClick={() =>
                        setIsFlexAlignYDropdownOpen(!isFlexAlignYDropdownOpen)
                      }
                    >
                      <span className="align-axis-label">Y</span>
                      <span className="align-value">
                        {selectedElement.styles?.alignItems === "flex-start"
                          ? "Top"
                          : selectedElement.styles?.alignItems === "center"
                          ? "Center"
                          : selectedElement.styles?.alignItems === "flex-end"
                          ? "Bottom"
                          : selectedElement.styles?.alignItems === "stretch"
                          ? "Stretch"
                          : selectedElement.styles?.alignItems === "baseline"
                          ? "Baseline"
                          : "Stretch"}
                      </span>
                      <span
                        className={`dropdown-arrow ${
                          isFlexAlignYDropdownOpen ? "open" : ""
                        }`}
                      >
                        ▼
                      </span>
                    </button>
                    {isFlexAlignYDropdownOpen && (
                      <div className="flex-align-dropdown-content">
                        <button
                          onClick={() =>
                            updateElementStyles(
                              selectedElement.id,
                              "alignItems",
                              "flex-start"
                            )
                          }
                        >
                          Top
                        </button>
                        <button
                          onClick={() =>
                            updateElementStyles(
                              selectedElement.id,
                              "alignItems",
                              "center"
                            )
                          }
                        >
                          Center
                        </button>
                        <button
                          onClick={() =>
                            updateElementStyles(
                              selectedElement.id,
                              "alignItems",
                              "flex-end"
                            )
                          }
                        >
                          Bottom
                        </button>
                        <button
                          onClick={() =>
                            updateElementStyles(
                              selectedElement.id,
                              "alignItems",
                              "stretch"
                            )
                          }
                        >
                          Stretch
                        </button>
                        <button
                          onClick={() =>
                            updateElementStyles(
                              selectedElement.id,
                              "alignItems",
                              "baseline"
                            )
                          }
                        >
                          Baseline
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex-property">
                <span className="flex-label">Gap</span>
                <div className="flex-gap-control">
                  <input
                    className="flex-gap-slider"
                    type="range"
                    min="0"
                    max="100"
                    value={
                      selectedElement.styles?.[selectedDevice]?.gap
                        ? parseInt(selectedElement.styles[selectedDevice].gap)
                        : selectedElement.styles?.gap
                        ? parseInt(selectedElement.styles.gap)
                        : 0
                    }
                    onChange={(e) => {
                      const value = e.target.value;

                      updateElementStyles(
                        selectedElement.id,
                        "gap",
                        `${value}px`,
                        null,
                        selectedDevice
                      );
                    }}
                  />

                  <div className="flex-gap-input">
                    <input
                      type="number"
                      value={
                        selectedElement.styles?.gap
                          ? parseInt(selectedElement.styles.gap)
                          : ""
                      }
                      onChange={(e) => {
                        const value = e.target.value;
                        updateElementStyles(
                          selectedElement.id,
                          "gap",
                          value === "" ? "" : `${value}${gapUnit}`
                        );
                      }}
                      min="0"
                    />
                    <div className="unit-dropdown">
                      <button
                        className="unit-button"
                        onClick={() => setIsGapUnitDropdownOpen(!isGapUnitDropdownOpen)}
                      >
                        {gapUnit}
                        <span className="dropdown-arrow">
                          {isGapUnitDropdownOpen ? "▼" : "▶"}
                        </span>
                      </button>
                      {isGapUnitDropdownOpen && (
                        <div className="unit-dropdown-content">
                          {sizeUnits.map((unit) => (
                            <button
                              key={unit}
                              onClick={() => {
                                handleGapUnitChange(unit);
                                setIsGapUnitDropdownOpen(false);
                              }}
                            >
                              {unit}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {selectedElement.styles?.display === "grid" && (
            <div className="grid-properties">
              <div className="grid-property">
                <span className="grid-label">Grid</span>
                <div className="grid-inputs">
                  <div className="grid-input-group">
                    <label>Columns</label>
                    <div className="grid-input-wrapper">
                      <input
                        type="number"
                        min="1"
                        value={getGridUnitCount(
                          selectedElement.styles?.gridTemplateColumns
                        )}
                        onChange={(e) => {
                          const count = Math.max(
                            1,
                            parseInt(e.target.value) || 1
                          );
                          updateElementStyles(
                            selectedElement.id,
                            "gridTemplateColumns",
                            `repeat(${count}, 1fr)`
                          );
                        }}
                      />
                      <div className="grid-input-arrows">
                        <button
                          type="button"
                          className="grid-arrow-btn grid-arrow-up"
                          onClick={(e) => {
                            e.preventDefault();
                            const currentCount = getGridUnitCount(
                              selectedElement.styles?.gridTemplateColumns
                            );
                            updateElementStyles(
                              selectedElement.id,
                              "gridTemplateColumns",
                              `repeat(${currentCount + 1}, 1fr)`
                            );
                          }}
                        >
                          ▲
                        </button>
                        <button
                          type="button"
                          className="grid-arrow-btn grid-arrow-down"
                          onClick={(e) => {
                            e.preventDefault();
                            const currentCount = getGridUnitCount(
                              selectedElement.styles?.gridTemplateColumns
                            );
                            const newCount = Math.max(1, currentCount - 1);
                            updateElementStyles(
                              selectedElement.id,
                              "gridTemplateColumns",
                              `repeat(${newCount}, 1fr)`
                            );
                          }}
                        >
                          ▼
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="grid-input-group">
                    <label>Rows</label>
                    <div className="grid-input-wrapper">
                      <input
                        type="number"
                        min="1"
                        value={getGridUnitCount(
                          selectedElement.styles?.gridTemplateRows
                        )}
                        onChange={(e) => {
                          const count = Math.max(
                            1,
                            parseInt(e.target.value) || 1
                          );
                          updateElementStyles(
                            selectedElement.id,
                            "gridTemplateRows",
                            `repeat(${count}, 1fr)`
                          );
                        }}
                      />
                      <div className="grid-input-arrows">
                        <button
                          type="button"
                          className="grid-arrow-btn grid-arrow-up"
                          onClick={(e) => {
                            e.preventDefault();
                            const currentCount = getGridUnitCount(
                              selectedElement.styles?.gridTemplateRows
                            );
                            updateElementStyles(
                              selectedElement.id,
                              "gridTemplateRows",
                              `repeat(${currentCount + 1}, 1fr)`
                            );
                          }}
                        >
                          ▲
                        </button>
                        <button
                          type="button"
                          className="grid-arrow-btn grid-arrow-down"
                          onClick={(e) => {
                            e.preventDefault();
                            const currentCount = getGridUnitCount(
                              selectedElement.styles?.gridTemplateRows
                            );
                            const newCount = Math.max(1, currentCount - 1);
                            updateElementStyles(
                              selectedElement.id,
                              "gridTemplateRows",
                              `repeat(${newCount}, 1fr)`
                            );
                          }}
                        >
                          ▼
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid-property">
                <span className="grid-label">Direction</span>
                <div className="grid-direction-options">
                  <button
                    className={`grid-option ${
                      selectedElement.styles?.gridAutoFlow === "row" ||
                      !selectedElement.styles?.gridAutoFlow
                        ? "active"
                        : ""
                    }`}
                    onClick={() =>
                      updateElementStyles(
                        selectedElement.id,
                        "gridAutoFlow",
                        "row"
                      )
                    }
                  >
                    Horizontal
                  </button>
                  <button
                    className={`grid-option ${
                      selectedElement.styles?.gridAutoFlow === "column"
                        ? "active"
                        : ""
                    }`}
                    onClick={() =>
                      updateElementStyles(
                        selectedElement.id,
                        "gridAutoFlow",
                        "column"
                      )
                    }
                  >
                    Vertical
                  </button>
                </div>
              </div>

              <div className="grid-property">
                <span className="grid-label">Align</span>
                <div className="grid-align-options">
                  <div className="grid-align-dropdown">
                    <button
                      className="grid-align-button"
                      onClick={() =>
                        setIsGridAlignXDropdownOpen(!isGridAlignXDropdownOpen)
                      }
                    >
                      <span className="align-axis-label">X</span>
                      <span className="align-value">
                        {selectedElement.styles?.justifyItems || "Stretch"}
                      </span>
                      <span
                        className={`dropdown-arrow ${
                          isGridAlignXDropdownOpen ? "open" : ""
                        }`}
                      >
                        ▼
                      </span>
                    </button>
                    {isGridAlignXDropdownOpen && (
                      <div className="grid-align-dropdown-content">
                        <button
                          onClick={() =>
                            updateElementStyles(
                              selectedElement.id,
                              "justifyItems",
                              "start"
                            )
                          }
                        >
                          Left
                        </button>
                        <button
                          onClick={() =>
                            updateElementStyles(
                              selectedElement.id,
                              "justifyItems",
                              "center"
                            )
                          }
                        >
                          Center
                        </button>
                        <button
                          onClick={() =>
                            updateElementStyles(
                              selectedElement.id,
                              "justifyItems",
                              "end"
                            )
                          }
                        >
                          Right
                        </button>
                        <button
                          onClick={() =>
                            updateElementStyles(
                              selectedElement.id,
                              "justifyItems",
                              "stretch"
                            )
                          }
                        >
                          Stretch
                        </button>
                        <button
                          onClick={() =>
                            updateElementStyles(
                              selectedElement.id,
                              "justifyItems",
                              "baseline"
                            )
                          }
                        >
                          Baseline
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="grid-align-dropdown">
                    <button
                      className="grid-align-button"
                      onClick={() =>
                        setIsGridAlignYDropdownOpen(!isGridAlignYDropdownOpen)
                      }
                    >
                      <span className="align-axis-label">Y</span>
                      <span className="align-value">
                        {selectedElement.styles?.alignItems || "Stretch"}
                      </span>
                      <span
                        className={`dropdown-arrow ${
                          isGridAlignYDropdownOpen ? "open" : ""
                        }`}
                      >
                        ▼
                      </span>
                    </button>
                    {isGridAlignYDropdownOpen && (
                      <div className="grid-align-dropdown-content">
                        <button
                          onClick={() =>
                            updateElementStyles(
                              selectedElement.id,
                              "alignItems",
                              "start"
                            )
                          }
                        >
                          Top
                        </button>
                        <button
                          onClick={() =>
                            updateElementStyles(
                              selectedElement.id,
                              "alignItems",
                              "center"
                            )
                          }
                        >
                          Center
                        </button>
                        <button
                          onClick={() =>
                            updateElementStyles(
                              selectedElement.id,
                              "alignItems",
                              "end"
                            )
                          }
                        >
                          Bottom
                        </button>
                        <button
                          onClick={() =>
                            updateElementStyles(
                              selectedElement.id,
                              "alignItems",
                              "stretch"
                            )
                          }
                        >
                          Stretch
                        </button>
                        <button
                          onClick={() =>
                            updateElementStyles(
                              selectedElement.id,
                              "alignItems",
                              "baseline"
                            )
                          }
                        >
                          Baseline
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid-property">
                <span className="grid-label">Gap</span>
                <div className="grid-gap-control">
                  <input
                    className="grid-gap-slider"
                    type="range"
                    min="0"
                    max="100"
                    value={
                      selectedElement.styles?.[selectedDevice]?.gap
                        ? parseInt(selectedElement.styles[selectedDevice].gap)
                        : selectedElement.styles?.gap
                        ? parseInt(selectedElement.styles.gap)
                        : 0
                    }
                    onChange={(e) => {
                      const value = e.target.value;

                      updateElementStyles(
                        selectedElement.id,
                        "gap",
                        `${value}px`,
                        null,
                        selectedDevice
                      );
                    }}
                  />
                  <div className="grid-gap-input">
                    <input
                      type="number"
                      value={
                        selectedElement.styles?.gap
                          ? parseInt(selectedElement.styles.gap)
                          : ""
                      }
                      onChange={(e) => {
                        const value = e.target.value;
                        updateElementStyles(
                          selectedElement.id,
                          "gap",
                          value === "" ? "" : `${value}${gapUnit}`
                        );
                      }}
                      min="0"
                    />
                    <div className="unit-dropdown">
                      <button
                        className="unit-button"
                        onClick={() => setIsGapUnitDropdownOpen(!isGapUnitDropdownOpen)}
                      >
                        {gapUnit}
                        <span className="dropdown-arrow">
                          {isGapUnitDropdownOpen ? "▼" : "▶"}
                        </span>
                      </button>
                      {isGapUnitDropdownOpen && (
                        <div className="unit-dropdown-content">
                          {sizeUnits.map((unit) => (
                            <button
                              key={unit}
                              onClick={() => {
                                handleGapUnitChange(unit);
                                setIsGapUnitDropdownOpen(false);
                              }}
                            >
                              {unit}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="property-section">
          <h4>Spacing</h4>
          <div className="spacing-controls">
            <div className="spacing-box-wrapper">
              <div className="spacing-visual">
                <div className="margin-box">
                  <div className="margin-label">Margin</div>
                  <button
                    className={`spacing-number margin-top ${
                      selectedElement.styles?.[selectedDevice]?.marginTop ===
                      "auto"
                        ? "auto"
                        : ""
                    }`}
                    onMouseDown={(e) => handleDragStart(e, "marginTop")}
                    onClick={(e) => handleNumberClick(e, "marginTop")}
                  >
                    {selectedElement.styles?.[selectedDevice]?.marginTop ===
                    "auto"
                      ? "auto"
                      : selectedElement.styles?.[selectedDevice]?.marginTop
                      ? parseInt(
                          selectedElement.styles[selectedDevice].marginTop
                        )
                      : selectedElement.styles?.marginTop === "auto"
                      ? "auto"
                      : selectedElement.styles?.marginTop
                      ? parseInt(selectedElement.styles.marginTop)
                      : "0"}
                  </button>
                  <button
                    className={`spacing-number margin-right ${
                      selectedElement.styles?.[selectedDevice]?.marginRight ===
                      "auto"
                        ? "auto"
                        : ""
                    }`}
                    onMouseDown={(e) => handleDragStart(e, "marginRight")}
                    onClick={(e) => handleNumberClick(e, "marginRight")}
                  >
                    {selectedElement.styles?.[selectedDevice]?.marginRight ===
                    "auto"
                      ? "auto"
                      : selectedElement.styles?.[selectedDevice]?.marginRight
                      ? parseInt(
                          selectedElement.styles[selectedDevice].marginRight
                        )
                      : selectedElement.styles?.marginRight === "auto"
                      ? "auto"
                      : selectedElement.styles?.marginRight
                      ? parseInt(selectedElement.styles.marginRight)
                      : "0"}
                  </button>
                  <button
                    className={`spacing-number margin-bottom ${
                      selectedElement.styles?.[selectedDevice]?.marginBottom ===
                      "auto"
                        ? "auto"
                        : ""
                    }`}
                    onMouseDown={(e) => handleDragStart(e, "marginBottom")}
                    onClick={(e) => handleNumberClick(e, "marginBottom")}
                  >
                    {selectedElement.styles?.[selectedDevice]?.marginBottom ===
                    "auto"
                      ? "auto"
                      : selectedElement.styles?.[selectedDevice]?.marginBottom
                      ? parseInt(
                          selectedElement.styles[selectedDevice].marginBottom
                        )
                      : selectedElement.styles?.marginBottom === "auto"
                      ? "auto"
                      : selectedElement.styles?.marginBottom
                      ? parseInt(selectedElement.styles.marginBottom)
                      : "0"}
                  </button>
                  <button
                    className={`spacing-number margin-left ${
                      selectedElement.styles?.[selectedDevice]?.marginLeft ===
                      "auto"
                        ? "auto"
                        : ""
                    }`}
                    onMouseDown={(e) => handleDragStart(e, "marginLeft")}
                    onClick={(e) => handleNumberClick(e, "marginLeft")}
                  >
                    {selectedElement.styles?.[selectedDevice]?.marginLeft ===
                    "auto"
                      ? "auto"
                      : selectedElement.styles?.[selectedDevice]?.marginLeft
                      ? parseInt(
                          selectedElement.styles[selectedDevice].marginLeft
                        )
                      : selectedElement.styles?.marginLeft === "auto"
                      ? "auto"
                      : selectedElement.styles?.marginLeft
                      ? parseInt(selectedElement.styles.marginLeft)
                      : "0"}
                  </button>

                  <div className="padding-box">
                    <div className="padding-label">Padding</div>
                    <button
                      className={`spacing-number padding-top ${
                        selectedElement.styles?.[selectedDevice]?.paddingTop ===
                        "auto"
                          ? "auto"
                          : ""
                      }`}
                      onMouseDown={(e) => handleDragStart(e, "paddingTop")}
                      onClick={(e) => handleNumberClick(e, "paddingTop")}
                    >
                      {selectedElement.styles?.[selectedDevice]?.paddingTop ===
                      "auto"
                        ? "auto"
                        : selectedElement.styles?.[selectedDevice]?.paddingTop
                        ? parseInt(
                            selectedElement.styles[selectedDevice].paddingTop
                          )
                        : selectedElement.styles?.paddingTop === "auto"
                        ? "auto"
                        : selectedElement.styles?.paddingTop
                        ? parseInt(selectedElement.styles.paddingTop)
                        : "0"}
                    </button>
                    <button
                      className={`spacing-number padding-right ${
                        selectedElement.styles?.[selectedDevice]
                          ?.paddingRight === "auto"
                          ? "auto"
                          : ""
                      }`}
                      onMouseDown={(e) => handleDragStart(e, "paddingRight")}
                      onClick={(e) => handleNumberClick(e, "paddingRight")}
                    >
                      {selectedElement.styles?.[selectedDevice]
                        ?.paddingRight === "auto"
                        ? "auto"
                        : selectedElement.styles?.[selectedDevice]?.paddingRight
                        ? parseInt(
                            selectedElement.styles[selectedDevice].paddingRight
                          )
                        : selectedElement.styles?.paddingRight === "auto"
                        ? "auto"
                        : selectedElement.styles?.paddingRight
                        ? parseInt(selectedElement.styles.paddingRight)
                        : "0"}
                    </button>
                    <button
                      className={`spacing-number padding-bottom ${
                        selectedElement.styles?.[selectedDevice]
                          ?.paddingBottom === "auto"
                          ? "auto"
                          : ""
                      }`}
                      onMouseDown={(e) => handleDragStart(e, "paddingBottom")}
                      onClick={(e) => handleNumberClick(e, "paddingBottom")}
                    >
                      {selectedElement.styles?.[selectedDevice]
                        ?.paddingBottom === "auto"
                        ? "auto"
                        : selectedElement.styles?.[selectedDevice]
                            ?.paddingBottom
                        ? parseInt(
                            selectedElement.styles[selectedDevice].paddingBottom
                          )
                        : selectedElement.styles?.paddingBottom === "auto"
                        ? "auto"
                        : selectedElement.styles?.paddingBottom
                        ? parseInt(selectedElement.styles.paddingBottom)
                        : "0"}
                    </button>
                    <button
                      className={`spacing-number padding-left ${
                        selectedElement.styles?.[selectedDevice]
                          ?.paddingLeft === "auto"
                          ? "auto"
                          : ""
                      }`}
                      onMouseDown={(e) => handleDragStart(e, "paddingLeft")}
                      onClick={(e) => handleNumberClick(e, "paddingLeft")}
                    >
                      {selectedElement.styles?.[selectedDevice]?.paddingLeft ===
                      "auto"
                        ? "auto"
                        : selectedElement.styles?.[selectedDevice]?.paddingLeft
                        ? parseInt(
                            selectedElement.styles[selectedDevice].paddingLeft
                          )
                        : selectedElement.styles?.paddingLeft === "auto"
                        ? "auto"
                        : selectedElement.styles?.paddingLeft
                        ? parseInt(selectedElement.styles.paddingLeft)
                        : "0"}
                    </button>

                    <div className="element-box">Element</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="property-section">
          <h4>Position</h4>
          <div className="position-properties">
            <div className="position-property">
              <span className="position-label">Position</span>
              <div className="position-dropdown">
                <select
                  className="position-type-select"
                  value={position}
                  onChange={(e) => {
                    handlePositionChange(e.target.value);
                  }}
                >
                  {["static", "relative", "absolute", "fixed", "sticky"].map(
                    (value) => (
                      <option key={value} value={value}>
                        {value.charAt(0).toUpperCase() + value.slice(1)}
                      </option>
                    )
                  )}
                </select>
              </div>
            </div>

            <div className="float-clear-row">
              <div className="float-property">
                <span className="float-label">Float</span>
                <div className="float-dropdown">
                  <select
                    className="float-select"
                    value={float}
                    onChange={(e) => {
                      handleFloatChange(e.target.value);
                    }}
                  >
                    {["none", "left", "right"].map(
                      (value) => (
                        <option key={value} value={value}>
                          {value.charAt(0).toUpperCase() + value.slice(1)}
                        </option>
                      )
                    )}
                  </select>
                </div>
              </div>

              <div className="clear-property">
                <span className="clear-label">Clear</span>
                <div className="clear-dropdown">
                  <select
                    className="clear-select"
                    value={clear}
                    onChange={(e) => {
                      handleClearChange(e.target.value);
                    }}
                  >
                    {["none", "left", "right", "both"].map(
                      (value) => (
                        <option key={value} value={value}>
                          {value.charAt(0).toUpperCase() + value.slice(1)}
                        </option>
                      )
                    )}
                  </select>
                </div>
              </div>
            </div>

            {position !== "static" && (
              <>
                <div className="position-offset-controls">
                  {["top", "right", "bottom", "left"].map((side) => (
                    <div key={side} className="position-offset-property">
                      <span className="position-offset-label">
                        {side.charAt(0).toUpperCase() + side.slice(1)}
                      </span>
                      <div className="position-input-group">
                        <input
                          type="text"
                          value={positionOffsets[side].value}
                          placeholder="0"
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === "" || /^-?\d*$/.test(value)) {
                              handlePositionOffsetsChange(side, value);
                            }
                          }}
                          onBlur={() => {
                            const newValue = positionOffsets[side].value
                              ? `${positionOffsets[side].value}${positionOffsets[side].unit}`
                              : "";

                            updateElementStyles(
                              selectedElement.id,
                              side,
                              newValue,
                              null,
                              selectedDevice
                            );
                          }}
                          disabled={positionOffsets[side].unit === "auto"}
                          className={positionOffsets[side].value === "" ? "empty-value" : ""}
                        />
                        <select
                          className="position-unit-select"
                          value={positionOffsets[side].unit}
                          onChange={(e) => {
                            e.stopPropagation();
                            const newUnit = e.target.value;
                            handlePositionUnitChange(side, newUnit);
                          }}
                        >
                          {sizeUnits.map((unit) => (
                            <option key={unit} value={unit}>
                              {unit}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="z-index-property">
                  <span className="z-index-label">Z-Index</span>
                  <div className="z-index-input-group">
                    <input
                      type="text"
                      value={zIndex}
                      placeholder="0"
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "" || /^-?\d*$/.test(value)) {
                          handleZIndexChange(value);
                        }
                      }}
                      className={zIndex === "" ? "empty-value" : ""}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="property-section">
          <h4>Typography</h4>
          <div className="typography-properties">
            <div className="typography-row">
              <div className="font-property">
                <span className="font-label">Font</span>
                <div className="font-dropdown">
                  <select
                    className="font-select"
                    value={selectedElement?.styles?.fontFamily || ''}
                    onChange={(e) => {
                      handleFontChange(e.target.value);
                    }}
                    style={{
                      backgroundColor: '#2a2a2a',
                      color: '#fff'
                    }}
                  >
                    <option value="">Select a font</option>
                    {siteFonts && siteFonts.length > 0 && (
                      <optgroup 
                        label="Custom Fonts" 
                        style={{
                          backgroundColor: '#333',
                          color: '#fff'
                        }}
                      >
                        {siteFonts.map(fontRef => (
                          <option 
                            key={fontRef.font._id} 
                            value={fontRef.font.family}
                            style={{ fontFamily: fontRef.font.family }}
                          >
                            {fontRef.font.name}
                          </option>
                        ))}
                      </optgroup>
                    )}
                    <optgroup 
                      label="Standard Fonts"
                      style={{
                        backgroundColor: '#333',
                        color: '#fff'
                      }}
                    >
                      <option value="Arial, sans-serif" style={{ fontFamily: 'Arial, sans-serif' }}>Arial</option>
                      <option value="Helvetica, sans-serif" style={{ fontFamily: 'Helvetica, sans-serif' }}>Helvetica</option>
                      <option value="Times New Roman, serif" style={{ fontFamily: 'Times New Roman, serif' }}>Times New Roman</option>
                      <option value="Georgia, serif" style={{ fontFamily: 'Georgia, serif' }}>Georgia</option>
                      <option value="Courier New, monospace" style={{ fontFamily: 'Courier New, monospace' }}>Courier New</option>
                      <option value="Verdana, sans-serif" style={{ fontFamily: 'Verdana, sans-serif' }}>Verdana</option>
                      <option value="Tahoma, sans-serif" style={{ fontFamily: 'Tahoma, sans-serif' }}>Tahoma</option>
                      <option value="Trebuchet MS, sans-serif" style={{ fontFamily: 'Trebuchet MS, sans-serif' }}>Trebuchet MS</option>
                      <option value="Impact, sans-serif" style={{ fontFamily: 'Impact, sans-serif' }}>Impact</option>
                      <option value="system-ui" style={{ fontFamily: 'system-ui' }}>System UI</option>
                    </optgroup>
                  </select>
                </div>
              </div>

              <div className="font-weight-property">
                <span className="font-weight-label">Weight</span>
                <div className="font-weight-dropdown">
                  <select
                    className="font-weight-select"
                    value={selectedElement?.styles?.fontWeight || ''}
                    onChange={(e) => {
                      handleFontWeightChange(e.target.value);
                    }}
                  >
                    <option value="">Default</option>
                    {/* Show specific weights for selected custom font if available */}
                    {selectedElement?.styles?.fontFamily && siteFonts && siteFonts.length > 0 && (() => {
                      const currentFont = siteFonts.find(fontRef => 
                        fontRef.font && selectedElement.styles.fontFamily.includes(fontRef.font.family.replace(/"/g, '').split(',')[0].trim())
                      );
                      
                      if (currentFont && currentFont.font.variants && currentFont.font.variants.length > 0) {
                        // Map font variants to weight options
                        const weightMap = {
                          'thin': '100',
                          '100': '100',
                          'extralight': '200',
                          '200': '200',
                          'light': '300',
                          '300': '300',
                          'regular': '400',
                          '400': '400',
                          'normal': '400',
                          'medium': '500',
                          '500': '500',
                          'semibold': '600',
                          '600': '600',
                          'bold': '700',
                          '700': '700',
                          'extrabold': '800',
                          '800': '800',
                          'black': '900',
                          '900': '900'
                        };
                        
                        // Filter out italic variants and only show weights
                        const availableWeights = currentFont.font.variants
                          .filter(variant => !variant.includes('italic'))
                          .map(variant => {
                            const weightValue = weightMap[variant] || '400';
                            return {
                              value: weightValue,
                              label: `${weightValue} - ${variant.charAt(0).toUpperCase() + variant.slice(1)}`
                            };
                          });
                        
                        return availableWeights.map(weight => (
                          <option key={weight.value} value={weight.value}>
                            {weight.label}
                          </option>
                        ));
                      }
                      
                      // Fallback to standard weights if no specific weights found
                      return fontWeights.map(weight => (
                        <option key={weight.value} value={weight.value}>
                          {weight.label}
                        </option>
                      ));
                    })()}
                    
                    {/* Show all weights if no custom font is selected */}
                    {(!selectedElement?.styles?.fontFamily || !siteFonts || siteFonts.length === 0) && 
                      fontWeights.map(weight => (
                        <option key={weight.value} value={weight.value}>
                          {weight.label}
                        </option>
                      ))
                    }
                  </select>
                </div>
              </div>
            </div>
            <div className="typography-row">
              <div className="font-size-property">
                <span className="font-size-label">Size</span>
                <div className="size-input-group">
                  <input
                    type="text"
                    value={fontSize}
                    placeholder="16"
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "" || /^[0-9]*$/.test(value)) {
                        handleFontSizeChange(value);
                      }
                    }}
                    className={fontSize === "" ? "empty-value" : ""}
                  />
                  <select
                    className="typography-unit-select"
                    value={fontSizeUnit}
                    onChange={(e) => {
                      handleFontSizeUnitChange(e.target.value);
                    }}
                  >
                    {fontUnits.map((unit) => (
                      <option key={unit} value={unit}>
                        {unit}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="line-height-property">
                <span className="line-height-label">Height</span>
                <div className="size-input-group">
                  <input
                    type="text"
                    value={lineHeight}
                    placeholder="24"
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "" || /^[0-9]*$/.test(value)) {
                        handleLineHeightChange(value);
                      }
                    }}
                    className={lineHeight === "" ? "empty-value" : ""}
                  />
                  <select
                    className="typography-unit-select"
                    value={lineHeightUnit}
                    onChange={(e) => {
                      handleLineHeightUnitChange(e.target.value);
                    }}
                  >
                    {fontUnits.map((unit) => (
                      <option key={unit} value={unit}>
                        {unit}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="typography-row">
              <div className="font-color-property">
                <span className="font-color-label">Color</span>
                <div className="color-input-group">
                  <input
                    type="color"
                    value={isRgba ? rgbaToHex(textColor) : textColor}
                    onChange={(e) => {
                      handleTextColorChange(e.target.value);
                    }}
                    style={{ cursor: 'pointer' }}
                  />
                  {/* <input
                    type="text"
                    value={textColor}
                    onChange={(e) => {
                      const newColor = e.target.value;
                      // Validate color format
                      if (/^#([A-Fa-f0-9]{3}|[A-Fa-f0-9]{6})$/.test(newColor) || 
                          /^rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*(?:,\s*(?:0|1|0?\.\d+))?\)$/.test(newColor)) {
                        handleTextColorChange(newColor);
                      } else {
                        // Just update displayed text without applying invalid color
                        setTextColor(newColor);
                      }
                    }}
                    onBlur={() => {
                      // Validate and correct on blur
                      if (!/^#([A-Fa-f0-9]{3}|[A-Fa-f0-9]{6})$/.test(textColor) && 
                          !/^rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*(?:,\s*(?:0|1|0?\.\d+))?\)$/.test(textColor)) {
                        setTextColor(initialColor);
                      }
                    }}
                    placeholder="#000000 or rgba(0,0,0,1)"
                  /> */}
                  <div className="alpha-control">
                    <input 
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={colorAlpha}
                      onChange={handleAlphaChange}
                      className="alpha-slider compact"
                      style={{
                        background: `linear-gradient(to right, rgba(0, 0, 0, 0), ${
                          isRgba ? rgbaToHex(textColor) : textColor
                        })`
                      }}
                    />
                    <span className="alpha-badge">{Math.round(colorAlpha * 100)}%</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="typography-row">
              <div className="text-align-property">
                <span className="text-align-label">Align</span>
                <div className="text-align-options">
                  <button
                    className={`text-align-option ${
                      textAlign === "left" ? "active" : ""
                    }`}
                    onClick={() => {
                      handleTextAlignChange("left");
                    }}
                    title="Left"
                  >
                    <i
                      className="fas fa-align-left"
                      style={{ color: "#fff" }}
                    ></i>
                  </button>

                  <button
                    className={`text-align-option ${
                      textAlign === "center" ? "active" : ""
                    }`}
                    onClick={() => {
                      handleTextAlignChange("center");
                    }}
                    title="Center"
                  >
                    <i
                      className="fas fa-align-center"
                      style={{ color: "#fff" }}
                    ></i>
                  </button>

                  <button
                    className={`text-align-option ${
                      textAlign === "right" ? "active" : ""
                    }`}
                    onClick={() => {
                      handleTextAlignChange("right");
                    }}
                    title="Right"
                  >
                    <i
                      className="fas fa-align-right"
                      style={{ color: "#fff" }}
                    ></i>
                  </button>

                  <button
                    className={`text-align-option ${
                      textAlign === "justify" ? "active" : ""
                    }`}
                    onClick={() => {
                      handleTextAlignChange("justify");
                    }}
                    title="Justify"
                  >
                    <i
                      className="fas fa-align-justify"
                      style={{ color: "#fff" }}
                    ></i>
                  </button>
                </div>
              </div>
            </div>
            <div className="typography-row">
              <div className="text-decoration-property">
                <span className="text-decoration-label">Decor</span>
                <div className="text-decoration-options">
                  <button
                    className={`text-decoration-option ${
                      textDecoration === "none" ? "active" : ""
                    }`}
                    onClick={() => {
                      handleTextDecorationChange("none");
                    }}
                    title="None"
                  >
                    <i className="fas fa-times" style={{ color: "#fff" }}></i>
                  </button>

                  <button
                    className={`text-decoration-option ${
                      textDecoration === "line-through" ? "active" : ""
                    }`}
                    onClick={() => {
                      handleTextDecorationChange("line-through");
                    }}
                    title="Strikethrough"
                  >
                    <i
                      className="fas fa-strikethrough"
                      style={{ color: "#fff" }}
                    ></i>
                  </button>

                  <button
                    className={`text-decoration-option ${
                      textDecoration === "underline" ? "active" : ""
                    }`}
                    onClick={() => {
                      handleTextDecorationChange("underline");
                    }}
                    title="Underline"
                  >
                    <i
                      className="fas fa-underline"
                      style={{ color: "#fff" }}
                    ></i>
                  </button>

                  <button
                    className={`text-decoration-option ${
                      textDecoration === "overline" ? "active" : ""
                    }`}
                    onClick={() => {
                      handleTextDecorationChange("overline");
                    }}
                    title="Overline"
                  >
                    <i
                      className="fas fa-grip-lines"
                      style={{ color: "#fff" }}
                    ></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="property-section">
          <h4>Border</h4>
          <Borders
            selectedElement={selectedElement}
            setSelectedElement={setSelectedElement}
            updateElementStyles={updateElementStyles}
            selectedDevice={selectedDevice}
            classStylesRegistry={classStylesRegistry}
            setClassStylesRegistry={setClassStylesRegistry}
            elements={elements}
            setElements={setElements}
          />
        </div>
        <div className="property-section">
          <h4>Background</h4>
          <Background
            selectedElement={selectedElement}
            updateElementStyles={updateElementStyles}
            selectedDevice={selectedDevice}
            classStylesRegistry={classStylesRegistry}
            setClassStylesRegistry={setClassStylesRegistry}
            appliedStylesRegistry={appliedStylesRegistry}
            setAppliedStylesRegistry={setAppliedStylesRegistry}
            assets={assets}
          />
        </div>
        <div className="property-section">
          <h4>Effects</h4>
          <Effects
            selectedElement={selectedElement}
            updateElementStyles={updateElementStyles}
            selectedDevice={selectedDevice}
            classStylesRegistry={classStylesRegistry}
            setClassStylesRegistry={setClassStylesRegistry}
          />
        </div>
        {popupInput.isOpen && (
          <>
            <div
              className="popup-overlay-inspector"
              onClick={() =>
                setPopupInput((prev) => ({ ...prev, isOpen: false }))
              }
            ></div>
            <div
              className="number-popup-spacing"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="number-popup-content">
                <div className="popup-header">
                  <span className="property-name">
                    {popupInput.property.charAt(0).toUpperCase() +
                      popupInput.property.slice(1)}
                  </span>
                  <button
                    className="popup-close"
                    onClick={() =>
                      setPopupInput((prev) => ({ ...prev, isOpen: false }))
                    }
                  >
                    ×
                  </button>
                </div>
                <div className="popup-body">
                  
                  <div className="value-display">
                    <span className="current-value">
                      {popupInput.value === "auto" ? "auto" : `${popupInput.value}${popupInput.unit}`}
                    </span>
                  </div>

                  <div className="slider-container">
                    <input
                      type="range"
                      min={
                        devicePropertyRanges[selectedDevice][
                          popupInput.property
                        ]?.min || -100
                      }
                      max={
                        devicePropertyRanges[selectedDevice][
                          popupInput.property
                        ]?.max || 100
                      }
                      value={popupInput.value === "auto" ? 0 : popupInput.value}
                      onChange={(e) => {
                        e.stopPropagation();
                        const value = e.target.value;
                        setPopupInput((prev) => ({ ...prev, value }));

                        updateElementStyles(
                          selectedElement.id,
                          popupInput.property,
                          `${value}${popupInput.unit}`,
                          null,
                          selectedDevice
                        );
                      }}
                      onMouseUp={(e) => {
                        const value = e.target.value;
                        if (selectedElement?.className) {
                          const classNames =
                            selectedElement.className.split(" ");
                          classNames.forEach((className) => {
                            const currentStyles =
                              classStylesRegistry[selectedDevice].get(
                                className
                              ) || {};
                            setClassStylesRegistry((prev) => ({
                              ...prev,
                              [selectedDevice]: new Map(
                                prev[selectedDevice]
                              ).set(className, {
                                ...currentStyles,
                                [popupInput.property]: `${value}${popupInput.unit}`,
                              }),
                            }));
                          });
                        }
                      }}
                    />
                    <div className="slider-markers">
                      <span>
                        {devicePropertyRanges[selectedDevice][
                          popupInput.property
                        ]?.min || -100}
                      </span>
                      <span>0</span>
                      <span>
                        {devicePropertyRanges[selectedDevice][
                          popupInput.property
                        ]?.max || 100}
                      </span>
                    </div>
                  </div>

                  <div className="input-group">
                    <div className="number-input-wrapper">
                      <input
                        type="text"
                        value={popupInput.value}
                        onChange={(e) => {
                          e.stopPropagation();
                          const value = e.target.value;

                          if (value === "" || /^-?\d*$/.test(value)) {
                            setPopupInput((prev) => ({ ...prev, value }));

                            if (value !== "" && value !== "auto") {
                              updateElementStyles(
                                selectedElement.id,
                                popupInput.property,
                                `${value}${popupInput.unit}`,
                                null,
                                selectedDevice
                              );
                            }
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <select
                        className="popup-unit-select"
                        value={popupInput.unit}
                        onChange={(e) => {
                          e.stopPropagation();
                          const newUnit = e.target.value;
                          handlePopupUnitChange(newUnit);
                        }}
                      >
                        {sizeUnits.filter(unit => unit !== "auto").map((unit) => (
                          <option key={unit} value={unit}>
                            {unit}
                          </option>
                        ))}
                      </select>
                    </div>
                    {popupInput.property?.startsWith("margin") && (
                      <button
                        className="auto-button"
                        tabIndex="-1"
                      onClick={(e) => {
                          e.stopPropagation();
                          const property = popupInput.property;
                          updateElementStyles(
                            selectedElement.id,
                            property,
                            "auto"
                          );
                          setPopupInput((prev) => ({
                            ...prev,
                            isOpen: false,
                          }));
                        }}
                      >
                        Auto
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
        <div className="property-section">
          <h4>Size</h4>
          <div className="size-properties">
            <div className="size-property">
              <span className="size-label">Width</span>
              <div className="size-input-group">
                <input
                  type="text"
                  value={widthInput}
                  placeholder="0"
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "" || /^[0-9]*$/.test(value)) {
                      handleWidthChange(value);
                    }
                  }}
                  onBlur={(e) => {
                    if (widthInput === "") {
                      updateElementStyles(
                        selectedElement.id,
                        "width",
                        "",
                        null,
                        selectedDevice
                      );
                    }
                  }}
                  onDoubleClick={(e) => handleNumberClick(e, "width")}
                />
                <div className="unit-dropdown">
                  <select
                    className="unit-select"
                    value={widthUnit}
                    onChange={(e) => {
                      const unit = e.target.value;
                      handleWidthUnitChange(unit);
                    }}
                  >
                    <option value="" disabled>Unit</option>
                    {sizeUnits.map((unit) => (
                      <option key={unit} value={unit}>
                        {unit}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="size-property">
              <span className="size-label">Height</span>
              <div className="size-input-group">
                <input
                  type="text"
                  value={heightInput}
                  placeholder="0"
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "" || /^[0-9]*$/.test(value)) {
                      handleHeightChange(value);
                    }
                  }}
                  onBlur={(e) => {
                    if (heightInput === "") {
                      updateElementStyles(
                        selectedElement.id,
                        "height",
                        "",
                        null,
                        selectedDevice
                      );
                    }
                  }}
                  onDoubleClick={(e) => handleNumberClick(e, "height")}
                />
                <div className="unit-dropdown">
                  <select
                    className="unit-select"
                    value={heightUnit}
                    onChange={(e) => {
                      const unit = e.target.value;
                      handleHeightUnitChange(unit);
                    }}
                  >
                    <option value="" disabled>Unit</option>
                    {sizeUnits.map((unit) => (
                      <option key={unit} value={unit}>
                        {unit}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="size-properties" style={{ marginTop: "8px" }}>
            <div className="size-property">
              <span className="size-label">Min W</span>
              <div className="size-input-group">
                <input
                  type="text"
                  value={minWidthInput}
                  placeholder="0"
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "" || /^[0-9]*$/.test(value)) {
                      handleMinWidthChange(value);
                    }
                  }}
                  onBlur={(e) => {
                    if (minWidthInput === "") {
                      updateElementStyles(
                        selectedElement.id,
                        "minWidth",
                        "",
                        null,
                        selectedDevice
                      );
                    }
                  }}
                  onDoubleClick={(e) => handleNumberClick(e, "minWidth")}
                />
                <div className="unit-dropdown">
                  <select
                    className="unit-select"
                    value={minWidthUnit}
                    onChange={(e) => {
                      const unit = e.target.value;
                      handleMinWidthUnitChange(unit);
                    }}
                  >
                    <option value="" disabled>Unit</option>
                    {sizeUnits.map((unit) => (
                      <option key={unit} value={unit}>
                        {unit}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="size-property">
              <span className="size-label">Min H</span>
              <div className="size-input-group">
                <input
                  type="text"
                  value={minHeightInput}
                  placeholder="0"
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "" || /^[0-9]*$/.test(value)) {
                      handleMinHeightChange(value);
                    }
                  }}
                  onBlur={(e) => {
                    if (minHeightInput === "") {
                      updateElementStyles(
                        selectedElement.id,
                        "minHeight",
                        "",
                        null,
                        selectedDevice
                      );
                    }
                  }}
                  onDoubleClick={(e) => handleNumberClick(e, "minHeight")}
                />
                <div className="unit-dropdown">
                  <select
                    className="unit-select"
                    value={minHeightUnit}
                    onChange={(e) => {
                      const unit = e.target.value;
                      handleMinHeightUnitChange(unit);
                    }}
                  >
                    <option value="" disabled>Unit</option>
                    {sizeUnits.map((unit) => (
                      <option key={unit} value={unit}>
                        {unit}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="size-properties" style={{ marginTop: "8px" }}>
            <div className="size-property">
              <span className="size-label">Max W</span>
              <div className="size-input-group">
                <input
                  type="text"
                  value={maxWidthInput}
                  placeholder="0"
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "" || /^[0-9]*$/.test(value)) {
                      handleMaxWidthChange(value);
                    }
                  }}
                  onBlur={(e) => {
                    if (maxWidthInput === "") {
                      updateElementStyles(
                        selectedElement.id,
                        "maxWidth",
                        "",
                        null,
                        selectedDevice
                      );
                    }
                  }}
                  onDoubleClick={(e) => handleNumberClick(e, "maxWidth")}
                />
                <div className="unit-dropdown">
                  <select
                    className="unit-select"
                    value={maxWidthUnit}
                    onChange={(e) => {
                      const unit = e.target.value;
                      handleMaxWidthUnitChange(unit);
                    }}
                  >
                    <option value="" disabled>Unit</option>
                    {sizeUnits.map((unit) => (
                      <option key={unit} value={unit}>
                        {unit}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="size-property">
              <span className="size-label">Max H</span>
              <div className="size-input-group">
                <input
                  type="text"
                  value={maxHeightInput}
                  placeholder="0"
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "" || /^[0-9]*$/.test(value)) {
                      handleMaxHeightChange(value);
                    }
                  }}
                  onBlur={(e) => {
                    if (maxHeightInput === "") {
                      updateElementStyles(
                        selectedElement.id,
                        "maxHeight",
                        "",
                        null,
                        selectedDevice
                      );
                    }
                  }}
                  onDoubleClick={(e) => handleNumberClick(e, "maxHeight")}
                />
                <div className="unit-dropdown">
                  <select
                    className="unit-select"
                    value={maxHeightUnit}
                    onChange={(e) => {
                      const unit = e.target.value;
                      handleMaxHeightUnitChange(unit);
                    }}
                  >
                    <option value="" disabled>Unit</option>
                    {sizeUnits.map((unit) => (
                      <option key={unit} value={unit}>
                        {unit}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="size-properties" style={{ marginTop: "8px" }}>
            <div className="overflow-property">
              <span className="overflow-label">Overflow</span>
              <div className="overflow-options">
                <button
                  className={`overflow-option ${
                    overflow === "visible" ? "active" : ""
                  }`}
                  onClick={() => handleOverflowChange("visible")}
                  title="Visible"
                >
                  <i className="fas fa-eye" style={{ color: "#fff" }}></i>
                </button>

                <button
                  className={`overflow-option ${
                    overflow === "hidden" ? "active" : ""
                  }`}
                  onClick={() => handleOverflowChange("hidden")}
                  title="Hidden"
                >
                  <i className="fas fa-eye-slash" style={{ color: "#fff" }}></i>
                </button>

                <button
                  className={`overflow-option ${
                    overflow === "clip" ? "active" : ""
                  }`}
                  onClick={() => handleOverflowChange("clip")}
                  title="Clip"
                >
                  <i className="fas fa-cut" style={{ color: "#fff" }}></i>
                </button>

                <button
                  className={`overflow-option ${
                    overflow === "scroll" ? "active" : ""
                  }`}
                  onClick={() => handleOverflowChange("scroll")}
                  title="Scroll"
                >
                  <i
                    className="fas fa-arrows-up-down"
                    style={{ color: "#fff" }}
                  ></i>
                </button>

                <button
                  className={`overflow-option ${
                    overflow === "auto" ? "active" : ""
                  }`}
                  onClick={() => handleOverflowChange("auto")}
                >
                  Auto
                </button>
              </div>
            </div>
          </div>

          <div className="size-properties" style={{ marginTop: "8px" }}>
            <div className="object-fit-property">
              <span className="object-fit-label">Object Fit</span>
              <div className="object-fit-dropdown">
                <select
                  className="object-fit-select"
                  value={objectFit}
                  onChange={(e) => {
                    handleObjectFitChange(e.target.value);
                  }}
                >
                  <option value="" disabled>Select fit</option>
                  {["fill", "contain", "cover", "none", "scale-down"].map(
                    (value) => (
                      <option key={value} value={value}>
                        {value.charAt(0).toUpperCase() + value.slice(1)}
                      </option>
                    )
                  )}
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Add CustomCssEditor at the end of the style panel */}
      <CustomCssEditor
        selectedElement={selectedElement}
        setSelectedElement={setSelectedElement}
        selectedDevice={selectedDevice}
        elements={elements}
        setElements={setElements}
      />
    </div>
  );
};

export default Style;
